import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import NotificationSystem from "react-notification-system";
import Card from "components/Card/Card.jsx";
import flashLogo from "assets/img/flash_color_logo_text@3x.png";
import "./addonEngine.css";

const API = process.env.REACT_APP_NOT_SECRET_CODE;
const removedCampaignStorageKey = "addon_engine_removed_campaign_ids";
const campaignInsightsPerPage = 20;

const tabs = [
  { key: "campaigns", label: "Retention" },
  { key: "insights", label: "Insights" },
  { key: "addons", label: "Add-ons" },
  { key: "entitlements", label: "Assignments" },
  { key: "rules", label: "Benefit Rules" },
  { key: "journeys", label: "Messaging Journeys" },
  { key: "expiry", label: "Expiry Report" },
  { key: "usage", label: "Usage" },
];

const addonDefaults = {
  key: "",
  name: "",
  description: "",
  type: "access",
  activation_type: "admin_assign",
  target_scope: "lead",
  price: 0,
  duration_value: "",
  duration_type: "",
  is_active: 1,
};

const entitlementDefaults = {
  addon_id: "",
  target_type: "lead",
  target_id: "",
  source_type: "admin_assign",
  starts_at: "",
  expires_at: "",
  payment_status: "",
  status: "active",
};

const ruleDefaults = {
  addon_id: "",
  name: "",
  benefit_type: "fixed_discount",
  order_type: "all",
  trigger_type: "manual",
  trigger_value: "",
  discount_value: 0,
  max_discount: "",
  min_order_value: "",
  usage_limit_per_user: "",
  priority: 100,
  stacking_policy: "best_only",
  is_active: 1,
};

const journeyDefaults = {
  addon_id: "",
  key: "",
  name: "",
  description: "",
  audience_type: "lead",
  trigger_type: "event",
  trigger_key: "",
  is_active: 1,
  steps: [
    {
      step_order: 1,
      name: "Reminder 1",
      channel: "push",
      delay_value: 0,
      delay_type: "minute",
      subject: "",
      message_body: "",
      is_active: 1,
    },
  ],
};

const campaignDefaults = {
  key: "",
  name: "",
  description: "",
  message_only: false,
  campaign_group: "retention",
  eligibility_scope: "all_leads",
  audience_type: "lead",
  journey_trigger_type: "signup_complete",
  audience_order_count: 1,
  audience_start_mode: "now_and_future",
  stop_on_order_placed: null,
  stop_on_profile_completed: false,
  benefit_type: "free_delivery",
  benefit_name: "",
  order_type: "all",
  order_trigger_type: "first_order",
  order_number: 1,
  discount_value: 0,
  max_discount: "",
  min_order_value: "",
  usage_limit_per_user: 1,
  usage_limit_total: "",
  starts_at: "",
  ends_at: "",
  priority: 100,
  stacking_policy: "best_only",
  is_active: 1,
  app_display: {
    is_enabled: false,
    app_description: "",
    modal_type: "offer",
    modal_style: "cutout",
    modal_title: "",
    modal_description: "",
    modal_media_type: "image",
    modal_media_source: "",
    modal_media_layout: "cover",
    modal_details_text: "",
    modal_button_id: "",
    modal_button_label: "",
    modal_button_url: "",
    modal_button_bg_color: "",
    modal_button_border_color: "",
    modal_button_label_color: "",
    standard_free_count: "",
    standard_discount_value: "",
    express_free_count: "",
    express_discount_value: "",
    consult_type: "",
    calls_type: "",
    calls_count: "",
  },
  steps: [
    {
      name: "Reminder 1",
      channel: "push",
      delay_value: 5,
      delay_type: "minute",
      subject: "",
      message_body: "",
      is_active: 1,
    },
    {
      name: "Reminder 2",
      channel: "email",
      delay_value: 1,
      delay_type: "day",
      subject: "",
      message_body: "",
      is_active: 1,
    },
    {
      name: "Reminder 3",
      channel: "sms",
      delay_value: 2,
      delay_type: "day",
      subject: "",
      message_body: "",
      is_active: 1,
    },
  ],
};

const expiryFilterDefaults = {
  status: "expiring",
  expiring_days: 7,
  addon_id: "",
  payment_status: "",
};

function getToken() {
  const token = JSON.parse(localStorage.getItem("user"));
  return token && token.token;
}

function request(method, url, token, data) {
  return axios({
    method,
    url: `${API}/${url}`,
    data,
    headers: {
      "Content-Type": "application/json",
      "X-Request-ID": "124",
      "X-Country-Code": "BD",
      "X-Client-Name": "WEBSITE",
      "X-Client-Version": "123",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => res.data);
}

function rowsFromResponse(response) {
  if (!response || !response.data) return [];
  return Array.isArray(response.data) ? response.data : response.data.data || [];
}

function readRemovedCampaignIds() {
  try {
    return JSON.parse(localStorage.getItem(removedCampaignStorageKey) || "[]").map(String);
  } catch (error) {
    return [];
  }
}

function rememberRemovedCampaignId(id) {
  const nextIds = [...new Set([...readRemovedCampaignIds(), String(id)])];
  localStorage.setItem(removedCampaignStorageKey, JSON.stringify(nextIds));
  return nextIds;
}

function visibleCampaignRows(rows, removedIds = readRemovedCampaignIds()) {
  const hiddenIds = new Set(removedIds.map(String));
  return rows.filter((campaign) => !hiddenIds.has(String(campaign.id)));
}

function paginationFromResponse(response, fallbackCount = 0) {
  const pagination = response && response.data && response.data.pagination ? response.data.pagination : {};
  return {
    page: pagination.page || pagination.current_page || 1,
    per_page: pagination.per_page || 20,
    total: typeof pagination.total !== "undefined" ? pagination.total : fallbackCount,
    total_pages: pagination.total_pages || pagination.last_page || 1,
    from: typeof pagination.from !== "undefined" ? pagination.from : (fallbackCount ? 1 : 0),
    to: typeof pagination.to !== "undefined" ? pagination.to : fallbackCount,
  };
}

function paginationForVisibleRows(response, rows) {
  const pagination = paginationFromResponse(response, rows.length);
  return {
    ...pagination,
    total: rows.length,
    from: rows.length ? 1 : 0,
    to: rows.length,
    total_pages: 1,
  };
}

function queryString(params) {
  return Object.keys(params)
    .filter((key) => params[key] !== "" && params[key] !== null && typeof params[key] !== "undefined")
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join("&");
}

function expiryReportFromResponse(response) {
  const data = response && response.data ? response.data : {};

  return {
    summary: data.summary || {},
    records: data.records && data.records.data ? data.records.data : [],
  };
}

const Field = ({ label, children, error, required }) => (
  <FormGroup validationState={error ? "error" : null}>
    <ControlLabel>
      {label}
      {required && <span className="addon-engine-required">*</span>}
    </ControlLabel>
    {children}
    {error && <p className="addon-engine-field-error">{error}</p>}
  </FormGroup>
);

const Hint = ({ children }) => <p className="addon-engine-hint">{children}</p>;

const hasText = (value) => String(value || "").trim().length > 0;

const isNumberAtLeast = (value, min) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= min;
};

const messageChannelLabels = {
  push: "Push",
  email: "Email",
  sms: "SMS",
};

const renderTemplateText = (value) =>
  String(value || "")
    .replace(/\{\{lead_name\}\}/g, "Nimali")
    .replace(/\{\{lead_id\}\}/g, "12345");

const templateValue = (value, fallback = "") => (value === undefined || value === null ? fallback : value);
const canPickSendTime = (delayType) => ["day", "week"].includes(delayType);

const firstFilledValue = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    if (hasText(values[index])) {
      return values[index];
    }
  }

  return "";
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const imageMimeFromBase64 = (value) => {
  if (value.indexOf("/9j/") === 0) return "image/jpeg";
  if (value.indexOf("R0lGOD") === 0) return "image/gif";
  if (value.indexOf("UklGR") === 0) return "image/webp";
  if (value.indexOf("PHN2Zy") === 0) return "image/svg+xml";
  return "image/png";
};

const normalizeImageSource = (value) => {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(text)) {
    return text.replace(/\s/g, "");
  }

  if (/^(https?:)?\/\//i.test(text) || /^cid:/i.test(text)) {
    return text;
  }

  const compactValue = text.replace(/\s/g, "");

  if (/^[a-z0-9+/]+=*$/i.test(compactValue) && compactValue.length > 40) {
    return `data:${imageMimeFromBase64(compactValue)};base64,${compactValue}`;
  }

  if (text.indexOf("/") === 0) {
    return text;
  }

  return text;
};

const emailStepAliases = (step = {}) => {
  const label = hasOwn(step, "cta_label") ? step.cta_label : firstFilledValue(step.button_text, step.buttonText);
  const url = hasOwn(step, "cta_url") ? step.cta_url : firstFilledValue(step.button_url, step.buttonUrl);

  return {
    cta_label: label,
    cta_url: url,
    button_text: label,
    button_url: url,
    buttonText: label,
    buttonUrl: url,
  };
};

const withCampaignStepAliases = (step = {}) => {
  const aliases = emailStepAliases(step);
  const sendTime = hasOwn(step, "send_time")
    ? step.send_time
    : firstFilledValue(step.sendTime, step.send_at_time, step.sendAtTime);
  const heroImageUrl = hasOwn(step, "hero_image_url")
    ? normalizeImageSource(step.hero_image_url)
    : firstFilledValue(step.heroImageUrl, step.hero_image_base64);

  return {
    ...step,
    ...aliases,
    hero_image_url: normalizeImageSource(heroImageUrl),
    send_time: sendTime,
    sendTime,
    send_at_time: sendTime,
  };
};

const linesToArray = (value) => String(value || "")
  .split("\n")
  .map((line) => line.trim())
  .filter(Boolean);

const arrayToLines = (value) => Array.isArray(value) ? value.join("\n") : "";

const optionalNumber = (value) => {
  if (value === "" || value === null || typeof value === "undefined") {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const appDisplayPayload = (display = {}) => {
  if (!display.is_enabled) {
    return {};
  }

  const modalData = {};
  const details = linesToArray(display.modal_details_text);
  const button = {};
  const delivery = [];
  const standardFreeCount = optionalNumber(display.standard_free_count);
  const standardDiscountValue = optionalNumber(display.standard_discount_value);
  const expressFreeCount = optionalNumber(display.express_free_count);
  const expressDiscountValue = optionalNumber(display.express_discount_value);
  const buttonBorderColor = hasText(display.modal_button_border_color)
    ? display.modal_button_border_color
    : display.modal_button_bg_color;

  if (hasText(display.modal_type)) modalData.type = display.modal_type;
  if (hasText(display.modal_style)) modalData.style = display.modal_style;
  if (hasText(display.modal_title)) modalData.title = display.modal_title;
  if (hasText(display.modal_description)) modalData.description = display.modal_description;
  if (details.length) modalData.details = details;

  if (hasText(display.modal_media_source)) {
    modalData.media = {
      type: display.modal_media_type || "image",
      source: display.modal_media_source,
      layout: display.modal_media_layout || "cover",
    };
  }

  if (hasText(display.modal_button_id)) button.id = display.modal_button_id;
  if (hasText(display.modal_button_label)) button.label = display.modal_button_label;
  if (hasText(display.modal_button_url)) button.url = display.modal_button_url;
  if (hasText(display.modal_button_bg_color) || hasText(buttonBorderColor)) {
    button.buttonStyle = {
      ...(hasText(display.modal_button_bg_color) ? { backgroundColor: display.modal_button_bg_color } : {}),
      ...(hasText(buttonBorderColor) ? { borderColor: buttonBorderColor } : {}),
    };
  }
  if (hasText(display.modal_button_label_color)) button.labelStyle = { color: display.modal_button_label_color };
  if (Object.keys(button).length) modalData.buttons = [button];

  if (standardFreeCount !== null || standardDiscountValue !== null) {
    delivery.push({
      type: "standard",
      freeCount: standardFreeCount || 0,
      discountValue: standardDiscountValue,
    });
  }

  if (expressFreeCount !== null || expressDiscountValue !== null) {
    delivery.push({
      type: "express",
      freeCount: expressFreeCount || 0,
      discountValue: expressDiscountValue,
    });
  }

  return cleanDeepObject({
    app_description: display.app_description,
    modalData: Object.keys(modalData).length ? modalData : null,
    delivery: delivery.length ? delivery : null,
    consult_type: display.consult_type,
    calls: display.calls_type ? {
      type: display.calls_type,
      count: display.calls_type === "count" ? optionalNumber(display.calls_count) : null,
    } : null,
  });
};

const cleanDeepObject = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map(cleanDeepObject).filter((item) => item !== null);
  }

  if (payload && typeof payload === "object") {
    return Object.keys(payload).reduce((result, key) => {
      const value = cleanDeepObject(payload[key]);
      if (value !== "" && value !== null && typeof value !== "undefined") {
        result[key] = value;
      }
      return result;
    }, {});
  }

  return payload;
};

const campaignPayload = (form = {}) => ({
  ...form,
  app_display: appDisplayPayload(form.app_display),
  steps: (form.steps || []).map(withCampaignStepAliases),
});

const newCampaignReminderStep = (index) => ({
  name: `Reminder ${index + 1}`,
  channel: "email",
  delay_value: 1,
  delay_type: "day",
  subject: "",
  message_body: "",
  is_active: 1,
});

const stopAfterOrderValue = (value) => {
  if (value === true || value === 1 || value === "1") return "yes";
  if (value === false || value === 0 || value === "0") return "no";
  return "na";
};

const stopAfterOrderFromValue = (value) => {
  if (value === "yes") return true;
  if (value === "no") return false;
  return null;
};

const normalizeCampaignName = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

function validateCampaignForm(form) {
  const errors = { steps: [] };
  const messages = [];

  const addError = (key, message) => {
    errors[key] = message;
    messages.push(message);
  };

  if (!hasText(form.name)) {
    addError("name", "Retention name is required.");
  }

  if (!form.message_only) {
    if (form.order_trigger_type === "nth_order" && !isNumberAtLeast(form.order_number, 1)) {
      addError("order_number", "Order number must be 1 or higher.");
    }

    if (form.benefit_type !== "free_delivery" && !isNumberAtLeast(form.discount_value, 1)) {
      addError("discount_value", "Enter a benefit value greater than 0.");
    }

    if (form.max_discount !== "" && !isNumberAtLeast(form.max_discount, 0)) {
      addError("max_discount", "Max discount cannot be negative.");
    }

    if (!isNumberAtLeast(form.usage_limit_per_user, 1)) {
      addError("usage_limit_per_user", "Usage per customer must be 1 or higher.");
    }

    if (form.starts_at && form.ends_at && new Date(form.starts_at) > new Date(form.ends_at)) {
      addError("ends_at", "End date must be after the start date.");
    }
  }

  if (!form.steps.length) {
    addError("steps_root", "Add at least one message, or cancel this campaign.");
  }

  form.steps.forEach((step, index) => {
    const stepErrors = {};
    const rowLabel = `R${index + 1}`;
    const channelLabel = messageChannelLabels[step.channel] || "Message";
    const requiresSubject = step.channel !== "sms";
    const hasSubject = hasText(step.subject);
    const hasMessage = hasText(step.message_body);

    if (!step.channel) {
      stepErrors.channel = "Choose a channel.";
    }

    if (!isNumberAtLeast(step.delay_value, 0)) {
      stepErrors.delay_value = "Delay must be 0 or higher.";
    }

    if (!step.delay_type) {
      stepErrors.delay_type = "Choose a delay type.";
    }

    if (!hasSubject && !hasMessage) {
      stepErrors.subject = "Unused message. Add content or delete this row.";
      stepErrors.message_body = "Unused message. Add content or delete this row.";
      messages.push(`${rowLabel} ${channelLabel}: add subject and message, or delete this unused row.`);
    } else {
      if (requiresSubject && !hasSubject) {
        stepErrors.subject = "Subject is required.";
        messages.push(`${rowLabel} ${channelLabel}: subject is required.`);
      }

      if (!hasMessage) {
        stepErrors.message_body = step.channel === "email" ? "Add the email body inside Design email." : "Message is required.";
        messages.push(step.channel === "email" ? `${rowLabel} ${channelLabel}: add the email body inside Design email.` : `${rowLabel} ${channelLabel}: message is required.`);
      }

    }

    errors.steps[index] = stepErrors;
  });

  return {
    errors,
    isValid: !messages.length,
    message: messages[0] || "",
  };
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function uniqueCampaignKey(name, campaigns, currentId) {
  const baseKey = slugify(name) || "retention_campaign";
  const existingKeys = campaigns
    .filter((campaign) => campaign.id !== currentId)
    .map((campaign) => campaign.key)
    .filter(Boolean);
  let candidate = baseKey;
  let copyNumber = 1;

  while (existingKeys.indexOf(candidate) !== -1) {
    candidate = `${baseKey}_${copyNumber}`;
    copyNumber += 1;
  }

  return candidate;
}

function objectValue(value) {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value) || {};
    } catch (error) {
      return {};
    }
  }
  return value;
}

function formatDateTimeLocal(value) {
  if (!value) return "";
  return String(value).replace(" ", "T").slice(0, 16);
}

function campaignEndDate(campaign) {
  const rule = campaign && campaign.benefit_rules && campaign.benefit_rules[0];
  return rule && rule.ends_at ? rule.ends_at : "";
}

function campaignEndDateObject(campaign) {
  const value = campaignEndDate(campaign);
  if (!value) return null;
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

function isCampaignExpired(campaign) {
  const date = campaignEndDateObject(campaign);
  return !!date && date.getTime() < Date.now();
}

function campaignExpiryLabel(campaign) {
  const date = campaignEndDate(campaign);
  if (!date) return "No expiry";
  return displayDate(date);
}

function campaignLifecycleStatus(campaign) {
  if (isCampaignExpired(campaign)) return "expired";
  return campaign.is_active ? "active" : "inactive";
}

function humanizeCampaignValue(value) {
  const text = String(value || "").replace(/_/g, " ").trim();
  return text || "-";
}

function campaignBenefitValueLabel(form) {
  const value = form.discount_value || 0;

  if (form.benefit_type === "free_delivery") return "Free delivery";
  if (form.benefit_type === "percentage_discount") return `${value}%`;
  if (form.benefit_type === "flash_points") return `${value} FlashPoints`;
  return `Rs. ${value}`;
}

function hasOpenCampaignWithName(name, campaigns, currentId) {
  const normalized = normalizeCampaignName(name);
  if (!normalized) return false;

  return campaigns.some((campaign) => {
    const campaignId = campaign.id;
    return campaignId !== currentId
      && normalizeCampaignName(campaign.name) === normalized
      && !isCampaignExpired(campaign);
  });
}

function appDisplayFromRuleConfig(config = {}) {
  const modalData = objectValue(config.modalData || config.modal_data);
  const media = objectValue(modalData.media);
  const firstButton = modalData.buttons && modalData.buttons[0] ? modalData.buttons[0] : {};
  const standardDelivery = Array.isArray(config.delivery) ? config.delivery.find((item) => item.type === "standard") || {} : {};
  const expressDelivery = Array.isArray(config.delivery) ? config.delivery.find((item) => item.type === "express") || {} : {};
  const calls = objectValue(config.calls);

  return {
    ...campaignDefaults.app_display,
    is_enabled: !!(
      config.description
      || config.modalData
      || config.modal_data
      || (Array.isArray(config.delivery) && config.delivery.length)
      || config.consult_type
      || config.consultType
      || config.calls
    ),
    app_description: config.description || "",
    modal_type: modalData.type || campaignDefaults.app_display.modal_type,
    modal_style: modalData.style || campaignDefaults.app_display.modal_style,
    modal_title: modalData.title || "",
    modal_description: modalData.description || "",
    modal_media_type: media.type || campaignDefaults.app_display.modal_media_type,
    modal_media_source: media.source || "",
    modal_media_layout: media.layout || campaignDefaults.app_display.modal_media_layout,
    modal_details_text: arrayToLines(modalData.details),
    modal_button_id: firstButton.id || "",
    modal_button_label: firstButton.label || "",
    modal_button_url: firstButton.url || "",
    modal_button_bg_color: firstButton.buttonStyle && firstButton.buttonStyle.backgroundColor ? firstButton.buttonStyle.backgroundColor : "",
    modal_button_border_color: firstButton.buttonStyle && firstButton.buttonStyle.borderColor ? firstButton.buttonStyle.borderColor : "",
    modal_button_label_color: firstButton.labelStyle && firstButton.labelStyle.color ? firstButton.labelStyle.color : "",
    standard_free_count: typeof standardDelivery.freeCount !== "undefined" ? standardDelivery.freeCount : "",
    standard_discount_value: typeof standardDelivery.discountValue !== "undefined" && standardDelivery.discountValue !== null ? standardDelivery.discountValue : "",
    express_free_count: typeof expressDelivery.freeCount !== "undefined" ? expressDelivery.freeCount : "",
    express_discount_value: typeof expressDelivery.discountValue !== "undefined" && expressDelivery.discountValue !== null ? expressDelivery.discountValue : "",
    consult_type: config.consult_type || config.consultType || "",
    calls_type: calls.type || "",
    calls_count: typeof calls.count !== "undefined" ? calls.count : "",
  };
}

function uniqueCampaignCopyName(name, campaigns) {
  const baseName = String(name || "Retention Campaign").trim() || "Retention Campaign";
  let copyNumber = 1;
  let candidate = `${baseName} ${copyNumber}`;

  while (hasOpenCampaignWithName(candidate, campaigns)) {
    copyNumber += 1;
    candidate = `${baseName} ${copyNumber}`;
  }

  return candidate;
}

function campaignToForm(campaign) {
  const config = objectValue(campaign.config_json);
  const rule = campaign.benefit_rules && campaign.benefit_rules[0];
  const ruleConfig = objectValue(rule && rule.config_json);
  const journey = campaign.messaging_journeys && campaign.messaging_journeys[0];
  const exitRules = objectValue(journey && journey.exit_rules_json);
  const steps = journey && journey.steps && journey.steps.length
    ? journey.steps.map((step) => {
        const stepConfig = objectValue(step.config_json) || {};
        const delayType = step.delay_type || stepConfig.delay_type || "minute";
        const normalizedStep = withCampaignStepAliases({
          ...stepConfig,
          id: step.id,
          name: step.name || stepConfig.name || "",
          channel: step.channel || stepConfig.channel || "push",
          delay_value: step.delay_value || stepConfig.delay_value || 0,
          delay_type: delayType,
          send_time: canPickSendTime(delayType)
            ? firstFilledValue(step.send_time, stepConfig.send_time, stepConfig.sendTime, stepConfig.send_at_time, stepConfig.sendAtTime)
            : "",
          subject: step.subject || stepConfig.subject || "",
          message_body: step.message_body || stepConfig.message_body || "",
          is_active: step.is_active ? 1 : 0,
        });

        return normalizedStep;
      })
    : campaignDefaults.steps;

  return {
    ...campaignDefaults,
    id: campaign.id,
    key: campaign.key || "",
    name: campaign.name || "",
    description: campaign.description || "",
    message_only: !!config.message_only || !rule,
    campaign_group: config.campaign_group || "retention",
    eligibility_scope: config.eligibility_scope || "all_leads",
    audience_type: journey && journey.audience_type ? journey.audience_type : "lead",
    journey_trigger_type: journey && journey.trigger_type ? journey.trigger_type : "signup_complete",
    audience_order_count: config.audience_order_count || 1,
    audience_start_mode: config.audience_start_mode || "now_and_future",
    stop_on_order_placed: Object.prototype.hasOwnProperty.call(exitRules, "stop_on_order_placed") ? !!exitRules.stop_on_order_placed : null,
    stop_on_profile_completed: !!exitRules.stop_on_profile_completed,
    benefit_type: rule && rule.benefit_type ? rule.benefit_type : "free_delivery",
    benefit_name: rule && rule.name ? rule.name : "",
    order_type: rule && rule.order_type ? rule.order_type : "all",
    order_trigger_type: rule && rule.trigger_type ? rule.trigger_type : "first_order",
    order_number: rule && rule.trigger_value ? rule.trigger_value : 1,
    discount_value: rule && rule.discount_value ? rule.discount_value : 0,
    max_discount: rule && rule.max_discount ? rule.max_discount : "",
    min_order_value: rule && rule.min_order_value ? rule.min_order_value : "",
    usage_limit_per_user: rule && rule.usage_limit_per_user ? rule.usage_limit_per_user : 1,
    usage_limit_total: rule && rule.usage_limit_total ? rule.usage_limit_total : "",
    starts_at: formatDateTimeLocal(rule && rule.starts_at),
    ends_at: formatDateTimeLocal(rule && rule.ends_at),
    priority: rule && rule.priority ? rule.priority : 100,
    stacking_policy: rule && rule.stacking_policy ? rule.stacking_policy : "best_only",
    is_active: campaign.is_active ? 1 : 0,
    app_display: appDisplayFromRuleConfig(ruleConfig),
    steps,
  };
}

function csvEscape(value) {
  const text = value === null || typeof value === "undefined" ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCampaignMessages(rows, filename) {
  const headers = [
    "Customer",
    "Lead ID",
    "Contact",
    "Channel",
    "Step",
    "Status",
    "Reason",
    "Scheduled At",
    "Sent At",
    "Opened At",
    "Clicked At",
    "Converted",
    "Converted Order",
    "Benefit Used",
    "Benefit Order",
  ];

  const lines = rows.map((message) => [
    message.customer_name || "",
    message.lead_id || "",
    message.customer_email || message.customer_phone || message.recipient || "",
    message.channel || "",
    message.step || "",
    message.status || "",
    message.error_message || "",
    displayDate(message.scheduled_at),
    displayDate(message.sent_at),
    displayDate(message.opened_at),
    displayDate(message.clicked_at),
    yesNo(message.converted),
    message.converted_order_id || "",
    yesNo(message.benefit_utilized),
    message.benefit_order_id || "",
  ].map(csvEscape).join(","));

  const csv = [headers.map(csvEscape).join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);

  link.href = URL.createObjectURL(blob);
  link.download = `${filename}-${date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

function displayDate(value) {
  if (!value) return "-";
  return String(value).replace("T", " ").slice(0, 19);
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

function displayBenefitValue(value) {
  if (value === null || typeof value === "undefined" || value === "") return "";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return `Rs. ${numeric.toFixed(2)}`;
}

function campaignInsightShell(campaign, overrides = {}) {
  const totals = campaign.totals || {};

  return {
    campaign: {
      id: campaign.campaign_id || campaign.id,
      name: campaign.campaign_name || campaign.name,
      key: campaign.campaign_key || campaign.key,
      is_active: campaign.is_active,
    },
    summary: {
      attempted: totals.messages || totals.sent || 0,
      delivered: totals.sent || 0,
      failed: totals.failed || 0,
      skipped: totals.skipped || 0,
      email_delivered: totals.email_sent || 0,
      push_delivered: totals.push_sent || 0,
      sms_delivered: totals.sms_sent || 0,
      opened: totals.email_opened || 0,
      clicked: (totals.email_clicked || 0) + (totals.push_clicked || 0),
      converted_customers: totals.converted_leads || 0,
      benefit_utilized_customers: totals.benefit_utilized_customers || 0,
    },
    pagination: {
      page: 1,
      per_page: 25,
      total: 0,
      total_pages: 1,
      from: 0,
      to: 0,
    },
    messages: [],
    ...overrides,
  };
}

function campaignChannels(campaign) {
  const journey = campaign.messaging_journeys && campaign.messaging_journeys[0];
  const steps = journey && journey.steps ? journey.steps : [];
  const channels = steps
    .filter((step) => step.is_active)
    .map((step) => step.channel)
    .filter(Boolean);

  return [...new Set(channels)];
}

function campaignAppContentEnabled(campaign) {
  const rule = campaign && campaign.benefit_rules && campaign.benefit_rules[0];
  const config = objectValue(rule && rule.config_json);
  return appDisplayFromRuleConfig(config).is_enabled;
}

const Status = ({ value }) => (
  <span className={`addon-engine-status ${value === 1 || value === true || value === "active" ? "active" : value || "inactive"}`}>
    {value === 1 || value === true ? "active" : value || "inactive"}
  </span>
);

const AddonEngine = () => {
  const notificationRef = React.createRef();
  const token = useMemo(() => getToken(), []);
  const [activeTab, setActiveTab] = useState("campaigns");
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignPagination, setCampaignPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1, from: 0, to: 0 });
  const [removedCampaignIds, setRemovedCampaignIds] = useState(readRemovedCampaignIds);
  const [campaignInsights, setCampaignInsights] = useState({ summary: {}, campaigns: [] });
  const [addons, setAddons] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [rules, setRules] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [orderBenefits, setOrderBenefits] = useState([]);
  const [expiryReport, setExpiryReport] = useState({ summary: {}, records: [] });
  const [expiryFilters, setExpiryFilters] = useState(expiryFilterDefaults);
  const [loadedTabs, setLoadedTabs] = useState({});
  const [benefitPreviewOrderId, setBenefitPreviewOrderId] = useState("");
  const [benefitPreview, setBenefitPreview] = useState(null);
  const [modal, setModal] = useState(null);
  const [addonForm, setAddonForm] = useState(addonDefaults);
  const [campaignForm, setCampaignForm] = useState(campaignDefaults);
  const [campaignErrors, setCampaignErrors] = useState({});
  const [campaignBuilderView, setCampaignBuilderView] = useState("list");
  const [campaignDetailReport, setCampaignDetailReport] = useState(null);
  const [campaignMetricReport, setCampaignMetricReport] = useState(null);
  const [campaignPendingRemoval, setCampaignPendingRemoval] = useState(null);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("");
  const [campaignChannelFilter, setCampaignChannelFilter] = useState("");
  const [campaignInsightSearch, setCampaignInsightSearch] = useState("");
  const [campaignRenameForm, setCampaignRenameForm] = useState(null);
  const [campaignRenameError, setCampaignRenameError] = useState("");
  const [campaignViewForm, setCampaignViewForm] = useState(null);
  const [campaignViewError, setCampaignViewError] = useState("");
  const [campaignEmailDesigner, setCampaignEmailDesigner] = useState(null);
  const [campaignAppDesigner, setCampaignAppDesigner] = useState(null);
  const [entitlementForm, setEntitlementForm] = useState(entitlementDefaults);
  const [ruleForm, setRuleForm] = useState(ruleDefaults);
  const [journeyForm, setJourneyForm] = useState(journeyDefaults);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const notify = (message, level = "success") => {
    if (notificationRef.current) {
      notificationRef.current.addNotification({ message, level, position: "bl", autoDismiss: 5 });
    }
  };

  const loadTabData = async (tab = activeTab, force = false) => {
    if (loadedTabs[tab] && !force) {
      return;
    }

    setLoading(true);
    try {
      if (tab === "addons") {
        const response = await request("get", "addons?without_pagination=1", token);
        setAddons(rowsFromResponse(response));
      }

      if (tab === "campaigns") {
        const [campaignRes, addonRes] = await Promise.all([
          request("get", "addon-campaigns?page=1&per_page=50", token),
          request("get", "addons?without_pagination=1", token),
        ]);
        const rows = visibleCampaignRows(rowsFromResponse(campaignRes), removedCampaignIds);
        setCampaigns(rows);
        setCampaignPagination(paginationForVisibleRows(campaignRes, rows));
        setAddons(rowsFromResponse(addonRes));
      }

      if (tab === "insights") {
        const response = await request("get", `addon-campaign-insights?page=1&per_page=${campaignInsightsPerPage}`, token);
        setCampaignInsights(response.data || { summary: {}, campaigns: [] });
      }

      if (tab === "entitlements") {
        const response = await request("get", "addon-entitlements", token);
        setEntitlements(rowsFromResponse(response));
      }

      if (tab === "rules") {
        const response = await request("get", "addon-benefit-rules", token);
        setRules(rowsFromResponse(response));
      }

      if (tab === "journeys") {
        const response = await request("get", "messaging-journeys", token);
        setJourneys(rowsFromResponse(response));
      }

      if (tab === "expiry") {
        const response = await request("get", `addon-expiry-report?${queryString(expiryFilters)}`, token);
        setExpiryReport(expiryReportFromResponse(response));
      }

      if (tab === "usage") {
        const [usageRes, orderBenefitsRes] = await Promise.all([
          request("get", "addon-usage-logs", token),
          request("get", "order-addon-benefits", token),
        ]);
        setUsageLogs(rowsFromResponse(usageRes));
        setOrderBenefits(rowsFromResponse(orderBenefitsRes));
      }

      setLoadedTabs((current) => ({ ...current, [tab]: true }));
    } catch (error) {
      notify("Unable to load retention data.", "error");
    }
    setLoading(false);
  };

  const loadCampaigns = async (page = 1, overrides = {}) => {
    const filters = {
      search: Object.prototype.hasOwnProperty.call(overrides, "search") ? overrides.search : campaignSearch,
      status: Object.prototype.hasOwnProperty.call(overrides, "status") ? overrides.status : campaignStatusFilter,
      channel: Object.prototype.hasOwnProperty.call(overrides, "channel") ? overrides.channel : campaignChannelFilter,
    };

    setLoading(true);
    try {
      const [campaignRes, addonRes] = await Promise.all([
        request("get", `addon-campaigns?${queryString({ page, per_page: 50, ...filters })}`, token),
        request("get", "addons?without_pagination=1", token),
      ]);
      const rows = visibleCampaignRows(rowsFromResponse(campaignRes), removedCampaignIds);
      setCampaigns(rows);
      setCampaignPagination(paginationForVisibleRows(campaignRes, rows));
      setAddons(rowsFromResponse(addonRes));
      setLoadedTabs((current) => ({ ...current, campaigns: true }));
    } catch (error) {
      notify("Unable to load retention campaigns.", "error");
    }
    setLoading(false);
  };

  const loadCampaignInsights = async (page = 1, search = campaignInsightSearch) => {
    setLoading(true);
    try {
      const response = await request("get", `addon-campaign-insights?${queryString({ page, per_page: campaignInsightsPerPage, search })}`, token);
      setCampaignInsights(response.data || { summary: {}, campaigns: [] });
      setLoadedTabs((current) => ({ ...current, insights: true }));
    } catch (error) {
      notify("Unable to load retention insights.", "error");
    }
    setLoading(false);
  };

  const loadExpiryReport = async (filters = expiryFilters) => {
    setLoading(true);
    try {
      const response = await request("get", `addon-expiry-report?${queryString(filters)}`, token);
      setExpiryReport(expiryReportFromResponse(response));
      setLoadedTabs((current) => ({ ...current, expiry: true }));
      notify("Expiry report refreshed.");
    } catch (error) {
      notify("Unable to load expiry report.", "error");
    }
    setLoading(false);
  };

  const previewBenefitOrder = async () => {
    if (!benefitPreviewOrderId) {
      notify("Please enter an order ID.", "warning");
      return;
    }

    setLoading(true);
    try {
      const response = await request("post", "addon-benefit-rules/preview-order", token, {
        order_id: benefitPreviewOrderId,
      });
      setBenefitPreview(response.data || response);
      notify("Order benefit preview loaded.");
    } catch (error) {
      notify("Unable to preview this order.", "error");
    }
    setLoading(false);
  };

  const saveAddon = async () => {
    await request("post", "addons", token, clean(addonForm));
    setModal(null);
    setAddonForm(addonDefaults);
    notify("Add-on created successfully.");
    loadTabData("addons", true);
  };

  const saveCampaign = async () => {
    const validation = validateCampaignForm(campaignForm);

    if (!validation.isValid) {
      setCampaignErrors(validation.errors);
      notify(validation.message || "Please complete the required retention fields.", "warning");
      return;
    }

    if (hasOpenCampaignWithName(campaignForm.name, campaigns, campaignForm.id)) {
      setCampaignErrors({ name: "An active or non-expired campaign already uses this name." });
      notify("A campaign with this name already exists. Use a different name or wait until the old one expires.", "warning");
      return;
    }

    setCampaignErrors({});

    const payload = campaignPayload({
      ...campaignForm,
      key: campaignForm.id ? campaignForm.key : uniqueCampaignKey(campaignForm.name, campaigns),
      description: campaignForm.description || "Created from Retention Campaigns.",
      eligibility_scope: "all_leads",
      campaign_group: campaignForm.campaign_group || "retention",
    });

    await request(campaignForm.id ? "put" : "post", campaignForm.id ? `addon-campaigns/${campaignForm.id}` : "addon-campaigns", token, cleanDeep(payload));
    notify(campaignForm.id ? "Retention campaign updated successfully." : "Retention campaign saved successfully.");
    setCampaignForm(campaignDefaults);
    setCampaignErrors({});
    setCampaignBuilderView("list");
    loadTabData("campaigns", true);
    loadTabData("insights", true);
    loadTabData("addons", true);
    loadTabData("rules", true);
    loadTabData("journeys", true);
  };

  const editCampaign = (campaign) => {
    setCampaignViewForm({ ...campaignToForm(campaign), campaign });
    setCampaignViewError("");
  };

  const createCampaign = (messageOnly = false) => {
    setCampaignForm({ ...campaignDefaults, message_only: messageOnly });
    setCampaignErrors({});
    setCampaignBuilderView("edit");
  };

  const duplicateCampaign = (campaign) => {
    const nextName = uniqueCampaignCopyName(campaign.name, campaigns);
    const duplicate = campaignToForm(campaign);

    setCampaignForm({
      ...duplicate,
      id: undefined,
      key: slugify(nextName),
      name: nextName,
      is_active: 0,
      steps: duplicate.steps.map((step) => ({
        ...step,
        id: undefined,
      })),
    });
    setCampaignErrors({});
    setCampaignBuilderView("edit");
    notify("Campaign duplicated as inactive. Review it, then save when ready.", "info");
  };

  const saveCampaignRename = async () => {
    if (!campaignRenameForm || !campaignRenameForm.campaign) {
      return;
    }

    const nextName = String(campaignRenameForm.name || "").trim();

    if (!nextName) {
      setCampaignRenameError("Retention name is required.");
      return;
    }

    if (hasOpenCampaignWithName(nextName, campaigns, campaignRenameForm.id)) {
      setCampaignRenameError("An active or non-expired campaign already uses this name.");
      return;
    }

    const payload = campaignPayload({
      ...campaignToForm(campaignRenameForm.campaign),
      name: nextName,
    });

    await request("put", `addon-campaigns/${campaignRenameForm.id}`, token, cleanDeep(payload));
    notify("Retention campaign renamed successfully.");
    setCampaignRenameForm(null);
    setCampaignRenameError("");
    loadTabData("campaigns", true);
    loadTabData("insights", true);
    loadTabData("addons", true);
    loadTabData("rules", true);
    loadTabData("journeys", true);
  };

  const saveCampaignViewChanges = async () => {
    if (!campaignViewForm || !campaignViewForm.campaign) {
      return;
    }

    const nextName = String(campaignViewForm.name || "").trim();

    if (!nextName) {
      setCampaignViewError("Retention name is required.");
      return;
    }

    if (hasOpenCampaignWithName(nextName, campaigns, campaignViewForm.id)) {
      setCampaignViewError("An active or non-expired campaign already uses this name.");
      return;
    }

    const validation = validateCampaignForm(campaignViewForm);
    if (!validation.isValid) {
      setCampaignViewError(validation.message || "Please complete the required retention fields.");
      notify(validation.message || "Please complete the required retention fields.", "warning");
      return;
    }

    const payload = campaignPayload({
      ...campaignViewForm,
      name: nextName,
    });
    delete payload.campaign;
    delete payload.preview_step_index;

    await request("put", `addon-campaigns/${campaignViewForm.id}`, token, cleanDeep(payload));
    notify("Retention campaign updated successfully. Future unsent messages will use the new copy.");
    setCampaignViewForm(null);
    setCampaignViewError("");
    loadTabData("campaigns", true);
    loadTabData("insights", true);
    loadTabData("addons", true);
    loadTabData("rules", true);
    loadTabData("journeys", true);
  };

  const loadCampaignDetailPage = async (report, changes = {}) => {
    const nextReport = { ...report, ...changes };
    const params = queryString({
      page: nextReport.page || 1,
      per_page: nextReport.perPage || 25,
      search: nextReport.search,
      channel: nextReport.channel,
      status: nextReport.status,
    });

    setCampaignDetailReport({ ...nextReport, loading: true });
    try {
      const response = await request("get", `addon-campaigns/${nextReport.campaign.id}/insight-detail?${params}`, token);
      setCampaignDetailReport((current) => {
        if (!current || current.campaign.id !== nextReport.campaign.id) {
          return current;
        }

        return {
          ...current,
          ...nextReport,
          detail: response.data || campaignInsightShell(nextReport.campaign, { unavailable: true }),
          loading: false,
        };
      });
    } catch (error) {
      setCampaignDetailReport((current) => {
        if (!current || current.campaign.id !== nextReport.campaign.id) {
          return current;
        }

        return {
          ...current,
          ...nextReport,
          detail: campaignInsightShell(nextReport.campaign, { unavailable: true }),
          loading: false,
        };
      });
      notify("Message-level breakdown is not available yet. Showing retention totals.", "warning");
    }
  };

  const loadCampaignDetail = async (campaign) => {
    const shell = campaignInsightShell(campaign);
    const report = {
      campaign: shell.campaign,
      detail: shell,
      loading: true,
      search: "",
      channel: "",
      status: "",
      page: 1,
      perPage: 25,
    };

    setCampaignDetailReport(report);
    loadCampaignDetailPage(report);
  };

  const loadCampaignMetricPage = async (report, changes = {}) => {
    const nextReport = { ...report, ...changes };
    const params = queryString({
      page: nextReport.page || 1,
      per_page: nextReport.perPage || 25,
      metric: nextReport.metric && nextReport.metric.key,
      search: nextReport.search,
    });

    setCampaignMetricReport({ ...nextReport, loading: true });

    try {
      const response = await request("get", `addon-campaigns/${nextReport.campaign.id}/insight-detail?${params}`, token);
      setCampaignMetricReport((current) => {
        if (!current || current.campaign.id !== nextReport.campaign.id || current.metric.key !== nextReport.metric.key) {
          return current;
        }

        return {
          ...current,
          ...nextReport,
          detail: response.data || campaignInsightShell(nextReport.campaign, { unavailable: true }),
          loading: false,
        };
      });
    } catch (error) {
      setCampaignMetricReport((current) => {
        if (!current || current.campaign.id !== nextReport.campaign.id || current.metric.key !== nextReport.metric.key) {
          return current;
        }

        return {
          ...current,
          ...nextReport,
          detail: campaignInsightShell(nextReport.campaign, { unavailable: true }),
          loading: false,
        };
      });
    }
  };

  const loadCampaignMetricReport = async (campaign, metric) => {
    const shell = campaignInsightShell(campaign);
    const report = {
      campaign: shell.campaign,
      metric,
      detail: shell,
      loading: true,
      search: "",
      page: 1,
      perPage: 25,
    };

    setCampaignMetricReport(report);
    loadCampaignMetricPage(report);
  };

  const deleteCampaign = async (campaign) => {
    if (!campaign) {
      return;
    }

    try {
      await request("delete", `addon-campaigns/${campaign.id}`, token);
      notify("Retention campaign removed successfully.");
      setCampaignPendingRemoval(null);
      setRemovedCampaignIds(rememberRemovedCampaignId(campaign.id));
      setCampaigns((current) => current.filter((item) => String(item.id) !== String(campaign.id)));
      setCampaignPagination((current) => {
        if (!current || typeof current.total === "undefined") {
          return current;
        }

        const nextTotal = Math.max(0, Number(current.total || 0) - 1);
        return {
          ...current,
          total: nextTotal,
          to: Math.min(Number(current.to || 0), nextTotal),
          from: nextTotal ? current.from : 0,
          total_pages: Math.max(1, Number(current.total_pages || 1)),
        };
      });
      if (campaignForm.id === campaign.id) {
        setCampaignForm(campaignDefaults);
        setCampaignBuilderView("list");
      }
      loadTabData("insights", true);
      loadTabData("addons", true);
      loadTabData("rules", true);
      loadTabData("journeys", true);
    } catch (error) {
      notify("Unable to remove retention campaign.", "error");
    }
  };

  const saveEntitlement = async () => {
    await request("post", "addon-entitlements", token, clean(entitlementForm));
    setModal(null);
    setEntitlementForm(entitlementDefaults);
    notify("Assignment created successfully.");
    loadTabData("entitlements", true);
  };

  const saveRule = async () => {
    await request("post", "addon-benefit-rules", token, clean(ruleForm));
    setModal(null);
    setRuleForm(ruleDefaults);
    notify("Benefit rule created successfully.");
    loadTabData("rules", true);
  };

  const saveJourney = async () => {
    await request("post", "messaging-journeys", token, clean(journeyForm));
    setModal(null);
    setJourneyForm(journeyDefaults);
    notify("Messaging journey created successfully.");
    loadTabData("journeys", true);
  };

  const deleteRecord = async (resource, id, label) => {
    if (!window.confirm(`Remove this ${label}?`)) {
      return;
    }

    try {
      await request("delete", `${resource}/${id}`, token);
      notify(`${label} removed successfully.`);
      const tabByResource = {
        addons: "addons",
        "addon-entitlements": "entitlements",
        "addon-benefit-rules": "rules",
        "messaging-journeys": "journeys",
      };
      loadTabData(tabByResource[resource] || activeTab, true);
    } catch (error) {
      notify(`Unable to remove ${label}.`, "error");
    }
  };

  const clean = (payload) =>
    Object.keys(payload).reduce((result, key) => {
      if (payload[key] !== "") result[key] = payload[key];
      return result;
    }, {});

  const cleanDeep = (payload) => {
    if (Array.isArray(payload)) {
      return payload.map(cleanDeep).filter((item) => item !== null);
    }

    if (payload && typeof payload === "object") {
      return Object.keys(payload).reduce((result, key) => {
        const value = cleanDeep(payload[key]);
        if (value !== "" && value !== null && typeof value !== "undefined") {
          result[key] = value;
        }
        return result;
      }, {});
    }

    return payload;
  };

  const addonOptions = addons.map((addon) => (
    <option key={addon.id} value={addon.id}>
      {addon.name} ({addon.key})
    </option>
  ));

  const updateJourneyStep = (index, key, value) => {
    const steps = journeyForm.steps.slice();
    steps[index] = { ...steps[index], [key]: value };
    setJourneyForm({ ...journeyForm, steps });
  };

  const updateCampaignStep = (index, key, value) => {
    const steps = campaignForm.steps.slice();
    steps[index] = {
      ...steps[index],
      [key]: value,
      ...(key === "cta_label" ? { button_text: value, buttonText: value } : {}),
      ...(key === "cta_url" ? { button_url: value, buttonUrl: value } : {}),
      ...(key === "send_time" ? { sendTime: value, send_at_time: value } : {}),
      ...(key === "delay_type" && !canPickSendTime(value) ? { send_time: "", sendTime: "", send_at_time: "" } : {}),
    };
    setCampaignForm({ ...campaignForm, steps });
    setCampaignErrors({});
  };

  const openCampaignEmailDesigner = (source, index) => {
    setCampaignEmailDesigner({ source, index });
  };

  const openCampaignAppDesigner = (source) => {
    setCampaignAppDesigner({ source });
  };

  const getCampaignEmailDesignerContext = () => {
    if (!campaignEmailDesigner) {
      return null;
    }

    const form = campaignEmailDesigner.source === "view" ? campaignViewForm : campaignForm;
    const step = form && form.steps ? form.steps[campaignEmailDesigner.index] : null;

    if (!form || !step) {
      return null;
    }

    return { form, step };
  };

  const getCampaignAppDesignerContext = () => {
    if (!campaignAppDesigner) {
      return null;
    }

    const form = campaignAppDesigner.source === "view" ? campaignViewForm : campaignForm;
    if (!form) {
      return null;
    }

    return { form, display: form.app_display || campaignDefaults.app_display };
  };

  const updateCampaignEmailDesignerField = (key, value) => {
    if (!campaignEmailDesigner) {
      return;
    }

    if (campaignEmailDesigner.source === "view") {
      if (!campaignViewForm || !campaignViewForm.steps[campaignEmailDesigner.index]) {
        return;
      }

      const steps = campaignViewForm.steps.slice();
      steps[campaignEmailDesigner.index] = {
        ...steps[campaignEmailDesigner.index],
        [key]: value,
        ...(key === "cta_label" ? { button_text: value, buttonText: value } : {}),
        ...(key === "cta_url" ? { button_url: value, buttonUrl: value } : {}),
        ...(key === "send_time" ? { sendTime: value, send_at_time: value } : {}),
      };
      setCampaignViewForm({ ...campaignViewForm, steps });
      setCampaignViewError("");
      return;
    }

    updateCampaignStep(campaignEmailDesigner.index, key, value);
  };

  const updateCampaignAppDesignerField = (key, value) => {
    if (!campaignAppDesigner) {
      return;
    }

    if (campaignAppDesigner.source === "view") {
      if (!campaignViewForm) {
        return;
      }

      setCampaignViewForm({
        ...campaignViewForm,
        app_display: {
          ...(campaignViewForm.app_display || campaignDefaults.app_display),
          [key]: value,
        },
      });
      setCampaignViewError("");
      return;
    }

    setCampaignForm({
      ...campaignForm,
      app_display: {
        ...(campaignForm.app_display || campaignDefaults.app_display),
        [key]: value,
      },
    });
    setCampaignErrors({});
  };

  const addCampaignStep = () => {
    setCampaignForm({
      ...campaignForm,
      steps: [
        ...campaignForm.steps,
        newCampaignReminderStep(campaignForm.steps.length),
      ],
    });
    setCampaignErrors({});
  };

  const removeCampaignStep = (index) => {
    if (!window.confirm("Remove this message from the retention campaign? Save the campaign to apply the change.")) {
      return;
    }

    setCampaignForm({
      ...campaignForm,
      steps: campaignForm.steps.filter((step, stepIndex) => stepIndex !== index),
    });
    setCampaignErrors({});
  };

  const addCampaignViewStep = () => {
    if (!campaignViewForm) {
      return;
    }

    setCampaignViewForm({
      ...campaignViewForm,
      steps: [
        ...campaignViewForm.steps,
        newCampaignReminderStep(campaignViewForm.steps.length),
      ],
    });
    setCampaignViewError("");
  };

  const removeCampaignViewStep = (index) => {
    if (!campaignViewForm) {
      return;
    }

    if (!window.confirm("Remove this message from the retention campaign? Save Changes to apply the change.")) {
      return;
    }

    setCampaignViewForm({
      ...campaignViewForm,
      steps: campaignViewForm.steps.filter((step, stepIndex) => stepIndex !== index),
    });
    setCampaignViewError("");

    if (campaignEmailDesigner && campaignEmailDesigner.source === "view" && campaignEmailDesigner.index === index) {
      setCampaignEmailDesigner(null);
    }
  };

  const updateCampaignName = (name) => {
    setCampaignForm({
      ...campaignForm,
      name,
      key: !campaignForm.id ? slugify(name) : campaignForm.key,
    });
    setCampaignErrors({});
  };

  const resetCampaignForm = () => {
    setCampaignForm(campaignDefaults);
    setCampaignErrors({});
    setCampaignBuilderView("list");
  };

  const addJourneyStep = () => {
    setJourneyForm({
      ...journeyForm,
      steps: [
        ...journeyForm.steps,
        {
          step_order: journeyForm.steps.length + 1,
          name: `Reminder ${journeyForm.steps.length + 1}`,
          channel: "email",
          delay_value: 24,
          delay_type: "hour",
          subject: "",
          message_body: "",
          is_active: 1,
        },
      ],
    });
  };

  const emailDesignerContext = getCampaignEmailDesignerContext();
  const appDesignerContext = getCampaignAppDesignerContext();

  return (
    <div className="main-content">
      <NotificationSystem ref={notificationRef} />
      <Grid fluid>
        <Row>
          <Col md={12}>
            <Card
              title="Retention Campaigns"
              category="Create and manage customer retention campaigns across Push, Email, and Sms."
              content={
                <>
                  <div className="addon-engine-tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        className={activeTab === tab.key ? "active" : ""}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  {loading && <p className="addon-engine-muted">Loading...</p>}
                  {activeTab === "campaigns" && renderCampaignBuilder(campaigns, campaignForm, setCampaignForm, updateCampaignName, resetCampaignForm, updateCampaignStep, addCampaignStep, saveCampaign, editCampaign, duplicateCampaign, setCampaignPendingRemoval, createCampaign, campaignBuilderView, campaignSearch, setCampaignSearch, campaignStatusFilter, setCampaignStatusFilter, campaignChannelFilter, setCampaignChannelFilter, campaignPagination, loading, loadCampaigns)}
                  {activeTab === "insights" && renderCampaignInsights(campaignInsights, () => loadCampaignInsights(1), loadCampaignDetail, loadCampaignMetricReport, loading, loadCampaignInsights, campaignInsightSearch, setCampaignInsightSearch)}
                  {activeTab === "addons" && renderAddons(addons, () => setModal("addon"), deleteRecord)}
                  {activeTab === "entitlements" && renderEntitlements(entitlements, () => setModal("entitlement"), deleteRecord)}
                  {activeTab === "rules" && renderRules(rules, () => setModal("rule"), deleteRecord, benefitPreviewOrderId, setBenefitPreviewOrderId, previewBenefitOrder, benefitPreview)}
                  {activeTab === "journeys" && renderJourneys(journeys, () => setModal("journey"), deleteRecord)}
                  {activeTab === "expiry" && renderExpiryReport(expiryReport, expiryFilters, setExpiryFilters, () => loadExpiryReport(expiryFilters), addons)}
                  {activeTab === "usage" && renderUsage(usageLogs, orderBenefits)}
                </>
              }
            />
          </Col>
        </Row>
      </Grid>

      <Modal show={modal === "addon"} onHide={() => setModal(null)} bsSize="large">
        <Modal.Header closeButton><Modal.Title>Create Add-on</Modal.Title></Modal.Header>
        <Modal.Body>{renderAddonForm(addonForm, setAddonForm)}</Modal.Body>
        <Modal.Footer><Button onClick={() => setModal(null)}>Cancel</Button><Button bsStyle="primary" onClick={saveAddon}>Save</Button></Modal.Footer>
      </Modal>

      <Modal show={modal === "entitlement"} onHide={() => setModal(null)} bsSize="large">
        <Modal.Header closeButton><Modal.Title>Assign Add-on</Modal.Title></Modal.Header>
        <Modal.Body>{renderEntitlementForm(entitlementForm, setEntitlementForm, addonOptions)}</Modal.Body>
        <Modal.Footer><Button onClick={() => setModal(null)}>Cancel</Button><Button bsStyle="primary" onClick={saveEntitlement}>Save</Button></Modal.Footer>
      </Modal>

      <Modal show={modal === "rule"} onHide={() => setModal(null)} bsSize="large">
        <Modal.Header closeButton><Modal.Title>Create Benefit Rule</Modal.Title></Modal.Header>
        <Modal.Body>{renderRuleForm(ruleForm, setRuleForm, addonOptions)}</Modal.Body>
        <Modal.Footer><Button onClick={() => setModal(null)}>Cancel</Button><Button bsStyle="primary" onClick={saveRule}>Save</Button></Modal.Footer>
      </Modal>

      <Modal show={modal === "journey"} onHide={() => setModal(null)} bsSize="large">
        <Modal.Header closeButton><Modal.Title>Create Messaging Journey</Modal.Title></Modal.Header>
        <Modal.Body>{renderJourneyForm(journeyForm, setJourneyForm, addonOptions, updateJourneyStep, addJourneyStep)}</Modal.Body>
        <Modal.Footer><Button onClick={() => setModal(null)}>Cancel</Button><Button bsStyle="primary" onClick={saveJourney}>Save</Button></Modal.Footer>
      </Modal>

      <Modal show={campaignBuilderView === "edit"} onHide={resetCampaignForm} dialogClassName="addon-engine-fullscreen-modal">
        <Modal.Header closeButton>
          <Modal.Title>{campaignForm.id ? `Edit Retention Campaign: ${campaignForm.name}` : "Create Retention Campaign"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderCampaignForm(campaignForm, setCampaignForm, updateCampaignName, updateCampaignStep, addCampaignStep, removeCampaignStep, campaignErrors, (index) => openCampaignEmailDesigner("create", index), () => openCampaignAppDesigner("create"))}</Modal.Body>
        <Modal.Footer>
          <Button onClick={resetCampaignForm}>Cancel</Button>
          <Button bsStyle="primary" onClick={saveCampaign}>{campaignForm.id ? "Update Retention" : "Save Retention"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignRenameForm} onHide={() => setCampaignRenameForm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Rename Retention Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Field label="Retention Name" required error={campaignRenameError}>
            <FormControl
              value={campaignRenameForm ? campaignRenameForm.name : ""}
              onChange={(e) => {
                setCampaignRenameForm({ ...campaignRenameForm, name: e.target.value });
                setCampaignRenameError("");
              }}
              placeholder="Retention name"
            />
          </Field>
          <p className="addon-engine-muted">Only the name can be changed here. Use Duplicate to create a new editable version.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setCampaignRenameForm(null)}>Cancel</Button>
          <Button bsStyle="primary" onClick={saveCampaignRename}>Save Name</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignViewForm} onHide={() => setCampaignViewForm(null)} dialogClassName="addon-engine-fullscreen-modal">
        <Modal.Header closeButton>
          <Modal.Title>{campaignViewForm ? `View Retention Campaign: ${campaignViewForm.campaign.name}` : "View Retention Campaign"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {campaignViewForm && renderCampaignView(campaignViewForm, setCampaignViewForm, campaignViewError, setCampaignViewError, (index) => openCampaignEmailDesigner("view", index), () => openCampaignAppDesigner("view"), addCampaignViewStep, removeCampaignViewStep)}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setCampaignViewForm(null)}>Close</Button>
          {campaignViewForm && (
            <Button
              bsStyle="info"
              onClick={() => {
                duplicateCampaign(campaignViewForm.campaign);
                setCampaignViewForm(null);
              }}
            >
              Duplicate
            </Button>
          )}
          <Button bsStyle="primary" onClick={saveCampaignViewChanges}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignMetricReport} onHide={() => setCampaignMetricReport(null)} dialogClassName="addon-engine-fullscreen-modal addon-engine-report-modal">
        <Modal.Header closeButton>
          <Modal.Title>{campaignMetricReport ? `${campaignMetricReport.metric.label}: ${campaignMetricReport.campaign.name}` : "Retention Report"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {campaignMetricReport && renderCampaignMetricReport(campaignMetricReport, (changes) => loadCampaignMetricPage(campaignMetricReport, changes))}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setCampaignMetricReport(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignDetailReport} onHide={() => setCampaignDetailReport(null)} dialogClassName="addon-engine-fullscreen-modal addon-engine-report-modal">
        <Modal.Header closeButton>
          <Modal.Title>{campaignDetailReport ? `Retention Breakdown: ${campaignDetailReport.campaign.name}` : "Retention Breakdown"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {campaignDetailReport && renderCampaignDetailReport(campaignDetailReport, (changes) => loadCampaignDetailPage(campaignDetailReport, changes))}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setCampaignDetailReport(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignEmailDesigner} onHide={() => setCampaignEmailDesigner(null)} dialogClassName="addon-engine-preview-modal addon-engine-email-designer-modal">
        <Modal.Header closeButton>
          <div className="addon-engine-preview-header">
            <Modal.Title>
              {emailDesignerContext ? `Design Email: ${emailDesignerContext.form.name || "Retention Campaign"} R${campaignEmailDesigner.index + 1}` : "Design Email"}
            </Modal.Title>
            <Button bsStyle="primary" bsSize="small" onClick={() => setCampaignEmailDesigner(null)}>Done</Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {emailDesignerContext && renderEmailDesigner(emailDesignerContext.step, updateCampaignEmailDesignerField)}
        </Modal.Body>
        <Modal.Footer>
          <span className="addon-engine-modal-footer-note">Email edits stay in this draft. Click Save Retention or Save Changes on the campaign to publish.</span>
          <Button bsStyle="primary" onClick={() => setCampaignEmailDesigner(null)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignAppDesigner} onHide={() => setCampaignAppDesigner(null)} dialogClassName="addon-engine-preview-modal addon-engine-email-designer-modal">
        <Modal.Header closeButton>
          <div className="addon-engine-preview-header">
            <Modal.Title>
              {appDesignerContext ? `Design App Display: ${appDesignerContext.form.name || "Retention Campaign"}` : "Design App Display"}
            </Modal.Title>
            <Button bsStyle="primary" bsSize="small" onClick={() => setCampaignAppDesigner(null)}>Done</Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {appDesignerContext && renderAppDisplayDesigner(appDesignerContext.form, appDesignerContext.display, updateCampaignAppDesignerField)}
        </Modal.Body>
        <Modal.Footer>
          <span className="addon-engine-modal-footer-note">App display edits stay in this draft. Save the campaign to publish them to customer offers.</span>
          <Button bsStyle="primary" onClick={() => setCampaignAppDesigner(null)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!campaignPendingRemoval} onHide={() => setCampaignPendingRemoval(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove Retention Campaign?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to remove
            {" "}
            <strong>{campaignPendingRemoval && campaignPendingRemoval.name}</strong>?
          </p>
          <p className="addon-engine-muted">This will stop future messages and checkout benefits for this retention campaign.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setCampaignPendingRemoval(null)}>Cancel</Button>
          <Button bsStyle="danger" onClick={() => deleteCampaign(campaignPendingRemoval)}>Remove Retention</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

function renderAddons(addons, openModal, deleteRecord) {
  return (
    <>
      <div className="addon-engine-toolbar">
        <strong>{addons.length} add-ons</strong>
        <Button bsStyle="primary" onClick={openModal}>Create Add-on</Button>
      </div>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Key</th><th>Name</th><th>Type</th><th>Activation</th><th>Target</th><th>Price</th><th>Status</th><th>Action</th></tr></thead>
        <tbody>{addons.map((row) => <tr key={row.id}><td>{row.key}</td><td>{row.name}</td><td>{row.type}</td><td>{row.activation_type}</td><td>{row.target_scope}</td><td>{row.price}</td><td><Status value={row.is_active} /></td><td><Button bsStyle="danger" bsSize="xsmall" onClick={() => deleteRecord("addons", row.id, "add-on")}>Delete</Button></td></tr>)}</tbody>
      </Table>
    </>
  );
}

function renderCampaignView(form, setForm, error, setError, openPreview = () => {}, openAppDesigner = () => {}, addStep = () => {}, removeStep = () => {}) {
  const stopAfterOrderLabel = {
    na: "Not applicable",
    yes: "Yes",
    no: "No",
  }[stopAfterOrderValue(form.stop_on_order_placed)];

  return (
    <>
      <div className="addon-engine-builder-section">
        <div className="addon-engine-section-title">
          <strong>Campaign Details</strong>
          <span>{form.message_only ? "Messages only" : "Benefit + messages"}</span>
        </div>
        <div className="addon-engine-form-grid addon-engine-details-grid">
          <Field label="Retention Name" required error={error}>
            <FormControl
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setError("");
              }}
            />
          </Field>
          <FormGroup className="addon-engine-toggle-field">
            <ControlLabel>Status</ControlLabel>
            <button
              className={`addon-engine-switch ${form.is_active === 1 || form.is_active === true || form.is_active === "1" ? "active" : ""}`}
              type="button"
              onClick={() => setForm({ ...form, is_active: form.is_active === 1 || form.is_active === true || form.is_active === "1" ? 0 : 1 })}
            >
              <span />
              {form.is_active === 1 || form.is_active === true || form.is_active === "1" ? "Active retention" : "Inactive retention"}
            </button>
            <Hint>Inactive campaigns are paused and should not send future messages.</Hint>
          </FormGroup>
          <div>
            <ControlLabel>When To Start</ControlLabel>
            <p className="addon-engine-readonly">{form.journey_trigger_type.replace(/_/g, " ")}</p>
          </div>
          <div>
            <ControlLabel>Stop After Order</ControlLabel>
            <p className="addon-engine-readonly">{stopAfterOrderLabel}</p>
          </div>
        </div>
      </div>

      {!form.message_only && (
        <div className="addon-engine-builder-section">
          <div className="addon-engine-section-title">
            <strong>Checkout Benefit</strong>
            <span>Read only</span>
          </div>
          <div className="addon-engine-summary-grid">
            <div><span>Benefit Type</span><strong>{humanizeCampaignValue(form.benefit_type)}</strong></div>
            <div><span>Benefit Value</span><strong>{campaignBenefitValueLabel(form)}</strong></div>
            <div><span>Usage Per Customer</span><strong>{form.usage_limit_per_user || "-"}</strong></div>
            <div><span>Total Usage</span><strong>{form.usage_limit_total || "No limit"}</strong></div>
            <div><span>Order Trigger</span><strong>{humanizeCampaignValue(form.order_trigger_type)}</strong></div>
            {form.order_trigger_type === "nth_order" && <div><span>Order Number</span><strong>{form.order_number || "-"}</strong></div>}
            <div><span>Order Type</span><strong>{humanizeCampaignValue(form.order_type)}</strong></div>
            <div><span>Minimum Order</span><strong>{form.min_order_value ? `Rs. ${form.min_order_value}` : "-"}</strong></div>
            <div><span>Max Discount</span><strong>{form.max_discount ? `Rs. ${form.max_discount}` : "-"}</strong></div>
            <div><span>Stacking</span><strong>{humanizeCampaignValue(form.stacking_policy)}</strong></div>
            <div><span>Starts</span><strong>{form.starts_at ? displayDate(form.starts_at) : "Immediately"}</strong></div>
            <div><span>Expires</span><strong>{campaignExpiryLabel(form.campaign)}</strong></div>
          </div>
        </div>
      )}

      {!form.message_only && renderAppDisplayAction(
        form,
        form.app_display || campaignDefaults.app_display,
        (key, value) => {
          setForm({
            ...form,
            app_display: {
              ...(form.app_display || campaignDefaults.app_display),
              [key]: value,
            },
          });
          setError("");
        },
        openAppDesigner
      )}

      <div className="addon-engine-builder-section">
        <div className="addon-engine-section-title">
          <strong>Messages</strong>
          <span>{form.steps.length} configured. Add, edit, or remove messages, then save changes.</span>
        </div>
        <div className="addon-engine-reminder-list">
          {form.steps.map((step, index) => {
            const steps = form.steps.slice();
            const isUnusedStep = !hasText(step.subject) && !hasText(step.message_body);
            const updateStep = (key, value) => {
              steps[index] = {
                ...steps[index],
                [key]: value,
                ...(key === "cta_label" ? { button_text: value, buttonText: value } : {}),
                ...(key === "cta_url" ? { button_url: value, buttonUrl: value } : {}),
                ...(key === "send_time" ? { sendTime: value, send_at_time: value } : {}),
                ...(key === "delay_type" && !canPickSendTime(value) ? { send_time: "", sendTime: "", send_at_time: "" } : {}),
              };
              setForm({ ...form, steps });
              setError("");
            };

            return (
              <div className="addon-engine-reminder addon-engine-view-message" key={index}>
                <div className="addon-engine-reminder-number">
                  <span>R{index + 1}</span>
                  <button className="addon-engine-remove-button" type="button" onClick={() => removeStep(index)}>{isUnusedStep ? "Delete unused" : "Remove"}</button>
                </div>
                <div>
                  <div className="addon-engine-reminder-grid">
                    <Field label="Channel" required>
                      <FormControl componentClass="select" value={step.channel} onChange={(e) => updateStep("channel", e.target.value)}>
                        <option value="push">Push</option>
                        <option value="email">Email</option>
                        <option value="sms">Sms</option>
                      </FormControl>
                    </Field>
                    <Field label="Wait for" required>
                      <FormControl type="number" value={step.delay_value} onChange={(e) => updateStep("delay_value", e.target.value)} />
                    </Field>
                    <Field label="Time unit" required>
                      <FormControl componentClass="select" value={step.delay_type} onChange={(e) => updateStep("delay_type", e.target.value)}>
                        <option value="minute">Minutes</option>
                        <option value="hour">Hours</option>
                        <option value="day">Days</option>
                        <option value="week">Weeks</option>
                      </FormControl>
                    </Field>
                    {canPickSendTime(step.delay_type) && (
                      <Field label="Send time">
                        <FormControl type="time" value={step.send_time || ""} onChange={(e) => updateStep("send_time", e.target.value)} />
                        <Hint>Optional. Pick a time of day, or leave blank to send as soon as the wait time is over.</Hint>
                      </Field>
                    )}
                    {step.channel !== "email" && (
                      <Field label="Subject" required={step.channel !== "sms"}>
                        <FormControl value={step.subject || ""} onChange={(e) => updateStep("subject", e.target.value)} placeholder={step.channel === "sms" ? "Not used for SMS" : "Shown as the email subject or push title"} />
                      </Field>
                    )}
                    {step.channel !== "email" && (
                      <Field label="Main message" required>
                        <FormControl componentClass="textarea" value={step.message_body || ""} onChange={(e) => updateStep("message_body", e.target.value)} />
                      </Field>
                    )}
                    {step.channel === "email" && renderEmailPreviewAction(index + 1, openPreview)}
                  </div>
                </div>
              </div>
            );
          })}
          {!form.steps.length && (
            <div className="addon-engine-empty">No messages added yet.</div>
          )}
          <button className="addon-engine-add-message" type="button" onClick={addStep}>
            <span>+</span>
            Add message
          </button>
        </div>
      </div>
    </>
  );
}

function renderEmailPreview(preview) {
  const step = preview.step || preview;
  const eyebrow = templateValue(step.eyebrow, "Welcome to Flash Health");
  const heroTitle = templateValue(step.hero_title, "Your first delivery is on us.");
  const heroImageSrc = normalizeImageSource(step.hero_image_url);
  const messageBody = hasText(step.message_body)
    ? step.message_body
    : "Hi {{lead_name}}, your first Flash Health delivery is free. Set up your profile and place your first order when you are ready.";
  const nextTitle = templateValue(step.next_title, "What to do next");
  const nextBody = templateValue(step.next_body, "Set up your medication profile and place your first order. The free delivery benefit will be applied automatically at checkout.");
  const disclaimer = templateValue(step.disclaimer, "No code required. Offer applies automatically to the first eligible order.");

  if (preview.loading) {
    return <div className="addon-engine-template-preview-state">Rendering the email template...</div>;
  }

  if (preview.error) {
    return <div className="addon-engine-validation-banner">{preview.error}</div>;
  }

  if (preview.html) {
    return (
      <div className="addon-engine-template-preview">
        <div className="addon-engine-email-subject">
          <span>Subject</span>
          <strong>{preview.subject || renderTemplateText(step.subject || "Flash Health")}</strong>
        </div>
        <iframe
          title="Rendered email preview"
          srcDoc={preview.html}
          sandbox=""
        />
      </div>
    );
  }

  return (
    <div className="addon-engine-message-preview">
      <div className="addon-engine-email-preview">
        <div className="addon-engine-email-subject">
          <span>Subject</span>
          <strong>{renderTemplateText(step.subject || "Flash Health")}</strong>
        </div>
        <div className="addon-engine-email-frame">
          <div className="addon-engine-email-logo">
            <img src={flashLogo} alt="Flash Health" />
          </div>
          <div className="addon-engine-email-body">
            <div>
              {hasText(eyebrow) && <span>{renderTemplateText(eyebrow)}</span>}
              {hasText(heroTitle) && <h3>{renderTemplateText(heroTitle)}</h3>}
              <p>Hi Nimali,</p>
              <p>{renderTemplateText(messageBody)}</p>
            </div>
            <div className="addon-engine-email-art">
              {heroImageSrc ? (
                <img src={heroImageSrc} alt="" />
              ) : (
                <strong>FIRST<br />FREE<br />DELIVERY</strong>
              )}
            </div>
          </div>
          {(hasText(nextTitle) || hasText(nextBody)) && (
            <div className="addon-engine-email-next">
              {hasText(nextTitle) && <strong>{renderTemplateText(nextTitle)}</strong>}
              {hasText(nextBody) && <p>{renderTemplateText(nextBody)}</p>}
            </div>
          )}
          {hasText(step.cta_label) && (
            <a className="addon-engine-email-cta" href={step.cta_url || "#preview"}>
              {renderTemplateText(step.cta_label)}
            </a>
          )}
          {hasText(disclaimer) && <em>{renderTemplateText(disclaimer)}</em>}
          <footer>
            +94 776 677 688 | listens@flash.health<br />
            Flash Health (Pvt) Ltd. 32/2, School Lane, Nawala, Sri Lanka.
          </footer>
        </div>
      </div>
    </div>
  );
}

function renderEmailDesigner(step, updateStep) {
  return (
    <div className="addon-engine-email-designer">
      <div className="addon-engine-email-designer-preview">
        {renderEmailPreview({ step })}
      </div>
      <div className="addon-engine-email-designer-controls">
        <div className="addon-engine-email-designer-title">
          <strong>Email design</strong>
          <span>Changes are kept in this campaign draft. Use Save Retention after closing to publish them.</span>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Subject</strong>
          <Field label="Email subject" required>
            <FormControl value={step.subject || ""} onChange={(e) => updateStep("subject", e.target.value)} placeholder="Shown as the email subject" />
          </Field>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Hero</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Small top text">
              <FormControl value={step.eyebrow || ""} onChange={(e) => updateStep("eyebrow", e.target.value)} placeholder="Welcome to Flash Health" />
            </Field>
            <Field label="Top image">
              <FormControl
                componentClass="textarea"
                value={step.hero_image_url || ""}
                onChange={(e) => updateStep("hero_image_url", e.target.value)}
                placeholder="https://... or data:image/png;base64,..."
              />
              <Hint>Paste an image URL, a data:image value, or raw base64 image data.</Hint>
            </Field>
          </div>
          <Field label="Main heading">
            <FormControl value={step.hero_title || ""} onChange={(e) => updateStep("hero_title", e.target.value)} placeholder="Your first delivery is on us." />
            <Hint>Clear this to hide the heading.</Hint>
          </Field>
        </div>

        <div className="addon-engine-email-control-section">
          <strong>Main message</strong>
          <Field label="Email body">
            <FormControl
              componentClass="textarea"
              value={step.message_body || ""}
              onChange={(e) => updateStep("message_body", e.target.value)}
              placeholder="Hi {{lead_name}}, your first Flash Health delivery is free..."
            />
            <Hint>This is the main customer message. A sample message is shown in preview until you write one.</Hint>
          </Field>
        </div>

        <div className="addon-engine-email-control-section">
          <strong>Next step section</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Section title">
              <FormControl value={step.next_title || ""} onChange={(e) => updateStep("next_title", e.target.value)} placeholder="What to do next" />
            </Field>
            <Field label="Footer note">
              <FormControl value={step.disclaimer || ""} onChange={(e) => updateStep("disclaimer", e.target.value)} placeholder="No code required..." />
            </Field>
          </div>
          <Field label="Section message">
            <FormControl componentClass="textarea" value={step.next_body || ""} onChange={(e) => updateStep("next_body", e.target.value)} placeholder="Set up your medication profile..." />
            <Hint>Clear the title and message to hide this section.</Hint>
          </Field>
        </div>

        <div className="addon-engine-email-control-section">
          <strong>Button</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Button text">
              <FormControl value={step.cta_label || ""} onChange={(e) => updateStep("cta_label", e.target.value)} placeholder="Set up my profile" />
              <Hint>Leave blank to hide the button.</Hint>
            </Field>
            <Field label="Button URL">
              <FormControl value={step.cta_url || ""} onChange={(e) => updateStep("cta_url", e.target.value)} placeholder="https://flash.health/..." />
              <Hint>Optional. Leave blank to use the default campaign link.</Hint>
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function appDisplayPreviewValues(form, display = {}) {
  return {
    title: display.modal_title || form.benefit_name || form.name || "Offer",
    description: display.app_description || display.modal_description || "Short offer message shown to the customer.",
    popupDescription: display.modal_description || display.app_description || "Explain the offer benefit and what the customer should do next.",
    buttonLabel: display.modal_button_label || "View Offer",
    buttonColor: display.modal_button_bg_color || "#4CAF50",
    buttonBorderColor: display.modal_button_border_color || display.modal_button_bg_color || "#4CAF50",
    buttonTextColor: display.modal_button_label_color || "#FFFFFF",
    mediaSource: display.modal_media_source || "",
    details: linesToArray(display.modal_details_text),
  };
}

function renderAppDisplayAction(form, appDisplay, updateAppDisplay, openDesigner) {
  const preview = appDisplayPreviewValues(form, appDisplay);
  const enabled = !!appDisplay.is_enabled;

  return (
    <div className="addon-engine-builder-section">
      <div className="addon-engine-section-title">
        <strong>App display</strong>
        <span>{enabled ? "Configured for the customer app" : "Optional offer card and popup"}</span>
      </div>
      <div className="addon-engine-app-summary">
        <label className="addon-engine-checkbox-line">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => updateAppDisplay("is_enabled", e.target.checked)}
          />
          <span>Show this offer in the app offer list and popup</span>
        </label>
        <div className="addon-engine-app-mini-preview">
          <div>
            <span>{enabled ? "Preview" : "Not shown in app yet"}</span>
            <strong>{preview.title}</strong>
            <p>{preview.description}</p>
          </div>
          {enabled && (
            <button type="button" style={{ backgroundColor: preview.buttonColor, borderColor: preview.buttonBorderColor, color: preview.buttonTextColor }}>
              {preview.buttonLabel}
            </button>
          )}
        </div>
        <Button bsStyle={enabled ? "primary" : "default"} onClick={openDesigner} disabled={!enabled}>
          Design app display
        </Button>
      </div>
    </div>
  );
}

function renderAppDisplayPreview(form, display) {
  const preview = appDisplayPreviewValues(form, display);

  return (
    <div className="addon-engine-app-preview-stage">
      <div className="addon-engine-phone-preview">
        <div className="addon-engine-phone-status" />
        <div className="addon-engine-phone-header">
          <strong>Available Offers</strong>
          <span>Flash Health</span>
        </div>
        <div className="addon-engine-offer-card-preview">
          <div>
            <span>{form.order_type === "all" ? "Eligible order" : form.order_type}</span>
            <strong>{preview.title}</strong>
            <p>{preview.description}</p>
          </div>
          <button type="button" style={{ backgroundColor: preview.buttonColor, borderColor: preview.buttonBorderColor, color: preview.buttonTextColor }}>
            {preview.buttonLabel}
          </button>
        </div>
        <div className={`addon-engine-popup-preview ${display.modal_style === "basic" ? "basic" : "cutout"}`}>
          {preview.mediaSource && (
            <div className="addon-engine-popup-media">
              <img src={preview.mediaSource} alt="" />
            </div>
          )}
          <strong>{preview.title}</strong>
          <p>{preview.popupDescription}</p>
          {!!preview.details.length && (
            <ul>
              {preview.details.slice(0, 3).map((detail, index) => <li key={index}>{detail}</li>)}
            </ul>
          )}
          <button type="button" style={{ backgroundColor: preview.buttonColor, borderColor: preview.buttonBorderColor, color: preview.buttonTextColor }}>
            {preview.buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderAppDisplayDesigner(form, display, updateDisplay) {
  return (
    <div className="addon-engine-email-designer addon-engine-app-designer">
      <div className="addon-engine-email-designer-preview">
        {renderAppDisplayPreview(form, display)}
      </div>
      <div className="addon-engine-email-designer-controls">
        <div className="addon-engine-email-designer-title">
          <strong>App display</strong>
          <span>Configure the offer card and popup shown from the customer offers API.</span>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Offer card</strong>
          <Field label="Offer description">
            <FormControl componentClass="textarea" value={display.app_description || ""} onChange={(e) => updateDisplay("app_description", e.target.value)} />
            <Hint>Short text shown under the offer name in the app.</Hint>
          </Field>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Popup</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Popup type">
              <FormControl componentClass="select" value={display.modal_type || "offer"} onChange={(e) => updateDisplay("modal_type", e.target.value)}>
                <option value="offer">Offer</option>
                <option value="info">Info</option>
                <option value="other">Other</option>
              </FormControl>
            </Field>
            <Field label="Popup style">
              <FormControl componentClass="select" value={display.modal_style || "cutout"} onChange={(e) => updateDisplay("modal_style", e.target.value)}>
                <option value="cutout">Cutout</option>
                <option value="basic">Basic</option>
              </FormControl>
            </Field>
          </div>
          <Field label="Popup title">
            <FormControl value={display.modal_title || ""} onChange={(e) => updateDisplay("modal_title", e.target.value)} placeholder={form.benefit_name || form.name} />
          </Field>
          <Field label="Popup description">
            <FormControl componentClass="textarea" value={display.modal_description || ""} onChange={(e) => updateDisplay("modal_description", e.target.value)} />
          </Field>
          <Field label="Popup details">
            <FormControl componentClass="textarea" value={display.modal_details_text || ""} onChange={(e) => updateDisplay("modal_details_text", e.target.value)} placeholder="One detail per line" />
          </Field>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Media</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Media type">
              <FormControl componentClass="select" value={display.modal_media_type || "image"} onChange={(e) => updateDisplay("modal_media_type", e.target.value)}>
                <option value="image">Image</option>
                <option value="lottie">Lottie</option>
              </FormControl>
            </Field>
            <Field label="Media layout">
              <FormControl componentClass="select" value={display.modal_media_layout || "cover"} onChange={(e) => updateDisplay("modal_media_layout", e.target.value)}>
                <option value="cover">Cover</option>
                <option value="center">Center</option>
              </FormControl>
            </Field>
          </div>
          <Field label="Media source">
            <FormControl value={display.modal_media_source || ""} onChange={(e) => updateDisplay("modal_media_source", e.target.value)} placeholder="https://..." />
          </Field>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Button</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Button ID">
              <FormControl value={display.modal_button_id || ""} onChange={(e) => updateDisplay("modal_button_id", e.target.value)} placeholder="order-medicines" />
            </Field>
            <Field label="Button label">
              <FormControl value={display.modal_button_label || ""} onChange={(e) => updateDisplay("modal_button_label", e.target.value)} placeholder="Order Medicines" />
            </Field>
            <Field label="Button deeplink">
              <FormControl value={display.modal_button_url || ""} onChange={(e) => updateDisplay("modal_button_url", e.target.value)} placeholder="flashhealthapp://..." />
            </Field>
            <Field label="Button color">
              <FormControl type="color" value={display.modal_button_bg_color || "#4CAF50"} onChange={(e) => updateDisplay("modal_button_bg_color", e.target.value)} />
            </Field>
            <Field label="Button border color">
              <FormControl type="color" value={display.modal_button_border_color || display.modal_button_bg_color || "#4CAF50"} onChange={(e) => updateDisplay("modal_button_border_color", e.target.value)} />
            </Field>
            <Field label="Button text color">
              <FormControl type="color" value={display.modal_button_label_color || "#FFFFFF"} onChange={(e) => updateDisplay("modal_button_label_color", e.target.value)} />
            </Field>
          </div>
        </div>
        <div className="addon-engine-email-control-section">
          <strong>Delivery and consult display</strong>
          <div className="addon-engine-email-control-grid">
            <Field label="Standard free count">
              <FormControl type="number" min="0" value={display.standard_free_count || ""} onChange={(e) => updateDisplay("standard_free_count", e.target.value)} />
            </Field>
            <Field label="Standard discount value">
              <FormControl type="number" min="0" value={display.standard_discount_value || ""} onChange={(e) => updateDisplay("standard_discount_value", e.target.value)} />
            </Field>
            <Field label="Express free count">
              <FormControl type="number" min="0" value={display.express_free_count || ""} onChange={(e) => updateDisplay("express_free_count", e.target.value)} />
            </Field>
            <Field label="Express discount value">
              <FormControl type="number" min="0" value={display.express_discount_value || ""} onChange={(e) => updateDisplay("express_discount_value", e.target.value)} />
            </Field>
            <Field label="Consult type">
              <FormControl componentClass="select" value={display.consult_type || ""} onChange={(e) => updateDisplay("consult_type", e.target.value)}>
                <option value="">Not applicable</option>
                <option value="talk_now">Talk now</option>
                <option value="talk_later">Talk later</option>
              </FormControl>
            </Field>
            <Field label="Calls type">
              <FormControl componentClass="select" value={display.calls_type || ""} onChange={(e) => updateDisplay("calls_type", e.target.value)}>
                <option value="">Not applicable</option>
                <option value="unlimited">Unlimited</option>
                <option value="count">Count</option>
              </FormControl>
            </Field>
            <Field label="Calls count">
              <FormControl type="number" min="0" value={display.calls_count || ""} onChange={(e) => updateDisplay("calls_count", e.target.value)} disabled={display.calls_type !== "count"} />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderEmailPreviewAction(stepNumber, openDesigner, error = "") {
  return (
    <div className="addon-engine-email-preview-action">
      <div>
        <strong>Email design</strong>
        <span>Open the email preview and edit the subject and content in one place.</span>
        {error && <span className="addon-engine-inline-error">{error}</span>}
      </div>
      <button type="button" onClick={() => openDesigner(stepNumber - 1)}>
        Design email
      </button>
    </div>
  );
}

function renderCampaignForm(form, setForm, updateName, updateStep, addStep, removeStep, errors = {}, openPreview = () => {}, openAppDesigner = () => {}) {
  const appDisplay = form.app_display || campaignDefaults.app_display;
  const updateAppDisplay = (key, value) => setForm({
    ...form,
    app_display: {
      ...appDisplay,
      [key]: value,
    },
  });

  return (
    <>
      <div className="addon-engine-mode-row">
        <button className={!form.message_only ? "active" : ""} onClick={() => setForm({ ...form, message_only: false })} type="button">Retention with benefit</button>
        <button className={form.message_only ? "active" : ""} onClick={() => setForm({ ...form, message_only: true })} type="button">Messages only</button>
      </div>

      <div className="addon-engine-builder-section">
        <div className="addon-engine-section-title">
          <strong>Retention Details</strong>
          <span>{form.message_only ? "Messages only" : "Benefit + messages"}</span>
        </div>
        <div className="addon-engine-form-grid addon-engine-details-grid">
          <Field label="Retention Name" required error={errors.name}>
            <FormControl
              value={form.name}
              onChange={(e) => updateName(e.target.value)}
              placeholder="Free First Delivery"
            />
          </Field>
          <FormGroup className="addon-engine-toggle-field">
            <ControlLabel>Status</ControlLabel>
            <button
              className={`addon-engine-switch ${form.is_active === 1 || form.is_active === true || form.is_active === "1" ? "active" : ""}`}
              type="button"
              onClick={() => setForm({ ...form, is_active: form.is_active === 1 || form.is_active === true || form.is_active === "1" ? 0 : 1 })}
            >
              <span />
              {form.is_active === 1 || form.is_active === true || form.is_active === "1" ? "Active retention" : "Inactive retention"}
            </button>
            <Hint>Turn this off to pause the retention campaign without deleting it.</Hint>
          </FormGroup>
          <Field label="Customer Condition">
            <FormControl
              componentClass="select"
              value={form.journey_trigger_type}
              onChange={(e) => setForm({
                ...form,
                journey_trigger_type: e.target.value,
                audience_order_count: e.target.value === "order_count_reached" ? (form.audience_order_count || form.order_number || 1) : form.audience_order_count,
              })}
            >
              <option value="signup_complete">Customer signs up</option>
              <option value="order_count_reached">Completed order count</option>
              <option value="order_completed">Order completed</option>
              <option value="profile_abandoned">Profile abandoned</option>
              <option value="pricing_viewed">Pricing viewed</option>
            </FormControl>
            {form.journey_trigger_type === "order_count_reached" && (
              <Hint>Customer becomes eligible when their completed order count matches the number below.</Hint>
            )}
          </Field>
          {form.journey_trigger_type === "order_count_reached" && (
            <>
              <Field label="Campaign Reach">
                <FormControl componentClass="select" value={form.audience_start_mode || "now_and_future"} onChange={(e) => setForm({ ...form, audience_start_mode: e.target.value })}>
                  <option value="now_and_future">Existing and future matching customers</option>
                  <option value="future_only">Future matching customers only</option>
                </FormControl>
                <Hint>Choose whether this campaign also applies to customers who already match today.</Hint>
              </Field>
              <Field label="Completed Orders" required>
                <FormControl type="number" min="1" value={form.audience_order_count || 1} onChange={(e) => setForm({ ...form, audience_order_count: e.target.value })} />
                <Hint>Use 1 for first-time customers, 2 for second-order customers, and so on.</Hint>
              </Field>
            </>
          )}
          <Field label="Stop After Order">
            <FormControl componentClass="select" value={stopAfterOrderValue(form.stop_on_order_placed)} onChange={(e) => setForm({ ...form, stop_on_order_placed: stopAfterOrderFromValue(e.target.value) })}>
              <option value="na">Not applicable</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </FormControl>
          </Field>
        </div>
      </div>

      {!form.message_only && (
        <div className="addon-engine-builder-section">
          <div className="addon-engine-section-title">
            <strong>Checkout Benefit</strong>
            <span>Applied automatically when the order matches</span>
          </div>
          <div className="addon-engine-form-grid">
            <Field label="Benefit Type">
              <FormControl componentClass="select" value={form.benefit_type} onChange={(e) => setForm({ ...form, benefit_type: e.target.value })}>
                <option value="free_delivery">Free delivery</option>
                <option value="fixed_discount">Fixed rupee discount</option>
                <option value="percentage_discount">Percentage discount</option>
                <option value="flash_points">FlashPoints</option>
              </FormControl>
            </Field>
            <Field label="Order Trigger">
              <FormControl componentClass="select" value={form.order_trigger_type} onChange={(e) => setForm({ ...form, order_trigger_type: e.target.value })}>
                <option value="first_order">First order</option>
                <option value="second_order">Second order</option>
                <option value="nth_order">Specific order number</option>
                <option value="manual">Manual/admin assigned</option>
              </FormControl>
            </Field>
            <Field label="Order Number" required={form.order_trigger_type === "nth_order"} error={errors.order_number}>
              <FormControl type="number" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} disabled={form.order_trigger_type !== "nth_order"} />
              <Hint>Use this for third delivery, sixth refill, etc.</Hint>
            </Field>
            <Field label="Value" required={form.benefit_type !== "free_delivery"} error={errors.discount_value}>
              <FormControl type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} disabled={form.benefit_type === "free_delivery"} />
              <Hint>For Rs. 1000 off, enter 1000. For 10%, enter 10.</Hint>
            </Field>
            <Field label="Max Discount" error={errors.max_discount}>
              <FormControl type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} />
            </Field>
            <Field label="Usage Per Customer" required error={errors.usage_limit_per_user}>
              <FormControl type="number" value={form.usage_limit_per_user} onChange={(e) => setForm({ ...form, usage_limit_per_user: e.target.value })} />
            </Field>
            <Field label="Order Type">
              <FormControl componentClass="select" value={form.order_type} onChange={(e) => setForm({ ...form, order_type: e.target.value })}>
                <option value="all">All order types</option>
                <option value="medicine">Medicine</option>
                <option value="lab">Lab</option>
              </FormControl>
            </Field>
            <Field label="Start Date">
              <FormControl type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
            </Field>
            <Field label="End Date" error={errors.ends_at}>
              <FormControl type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
            </Field>
          </div>
        </div>
      )}

      {!form.message_only && renderAppDisplayAction(form, appDisplay, updateAppDisplay, openAppDesigner)}

      <div className="addon-engine-builder-section">
        <div className="addon-engine-section-title">
          <strong>Messages</strong>
          <span>Fill each message or delete unused rows before saving</span>
        </div>
        {errors.steps_root && <div className="addon-engine-validation-banner">{errors.steps_root}</div>}
        <div className="addon-engine-reminder-list">
          {form.steps.map((step, index) => {
            const stepErrors = (errors.steps && errors.steps[index]) || {};
            const isUnusedStep = !hasText(step.subject) && !hasText(step.message_body);

            return (
              <div className={`addon-engine-reminder ${Object.keys(stepErrors).length ? "has-error" : ""}`} key={index}>
                <div className="addon-engine-reminder-number">
                  <span>R{index + 1}</span>
                  <button className="addon-engine-remove-button" type="button" onClick={() => removeStep(index)}>{isUnusedStep ? "Delete unused" : "Remove"}</button>
                </div>
                <div className="addon-engine-reminder-grid">
                  <Field label="Channel" required error={stepErrors.channel}>
                    <FormControl componentClass="select" value={step.channel} onChange={(e) => updateStep(index, "channel", e.target.value)}>
                      <option value="push">Push</option>
                      <option value="email">Email</option>
                      <option value="sms">Sms</option>
                    </FormControl>
                  </Field>
                  <Field label="Wait for" required error={stepErrors.delay_value}>
                    <FormControl type="number" value={step.delay_value} onChange={(e) => updateStep(index, "delay_value", e.target.value)} />
                  </Field>
                  <Field label="Time unit" required error={stepErrors.delay_type}>
                    <FormControl
                      componentClass="select"
                      value={step.delay_type}
                      onChange={(e) => updateStep(index, "delay_type", e.target.value)}
                    >
                      <option value="minute">Minutes</option>
                      <option value="hour">Hours</option>
                      <option value="day">Days</option>
                      <option value="week">Weeks</option>
                    </FormControl>
                  </Field>
                  {canPickSendTime(step.delay_type) && (
                    <Field label="Send time">
                      <FormControl type="time" value={step.send_time || ""} onChange={(e) => updateStep(index, "send_time", e.target.value)} />
                      <Hint>Optional. Pick a time of day, or leave blank to send as soon as the wait time is over.</Hint>
                    </Field>
                  )}
                  {step.channel !== "email" && (
                    <Field label="Subject" required={step.channel !== "sms"} error={stepErrors.subject}>
                      <FormControl value={step.subject || ""} onChange={(e) => updateStep(index, "subject", e.target.value)} placeholder={step.channel === "sms" ? "Not used for SMS" : "Shown as the email subject or push title"} />
                    </Field>
                  )}
                  {step.channel !== "email" && (
                    <Field label="Main message" required error={stepErrors.message_body}>
                      <FormControl componentClass="textarea" value={step.message_body || ""} onChange={(e) => updateStep(index, "message_body", e.target.value)} />
                    </Field>
                  )}
                  {step.channel === "email" && renderEmailPreviewAction(index + 1, openPreview, stepErrors.subject || stepErrors.message_body)}
                </div>
              </div>
            );
          })}
          {!form.steps.length && (
            <div className="addon-engine-empty">No messages added yet.</div>
          )}
          <button className="addon-engine-add-message" type="button" onClick={addStep}>
            <span>+</span>
            Add message
          </button>
        </div>
      </div>
    </>
  );
}

function renderCampaignBuilder(campaigns, form, setForm, updateName, resetForm, updateStep, addStep, saveCampaign, editCampaign, duplicateCampaign, deleteCampaign, createCampaign, view, campaignSearch, setCampaignSearch, campaignStatusFilter, setCampaignStatusFilter, campaignChannelFilter, setCampaignChannelFilter, pagination = {}, loading = false, loadCampaigns = () => {}) {
  const page = pagination.page || 1;
  const totalPages = pagination.total_pages || 1;
  const total = typeof pagination.total !== "undefined" ? pagination.total : campaigns.length;
  const from = typeof pagination.from !== "undefined" ? pagination.from : (campaigns.length ? 1 : 0);
  const to = typeof pagination.to !== "undefined" ? pagination.to : campaigns.length;

  return (
    <>
      <div className="addon-engine-start-row">
        <Button bsStyle="primary" onClick={() => createCampaign(false)}>Create Retention with Benefit</Button>
        <Button bsStyle="default" onClick={() => createCampaign(true)}>Create Messages Only</Button>
      </div>

      <div className="addon-engine-builder-section">
        <div className="addon-engine-section-title">
          <strong>Retention Campaigns</strong>
        </div>
        <div className="addon-engine-table-controls">
          <FormControl
            value={campaignSearch}
            onChange={(e) => {
              setCampaignSearch(e.target.value);
              loadCampaigns(1, { search: e.target.value });
            }}
            placeholder="Search by retention name"
          />
          <FormControl
            componentClass="select"
            value={campaignStatusFilter}
            onChange={(e) => {
              setCampaignStatusFilter(e.target.value);
              loadCampaigns(1, { status: e.target.value });
            }}
          >
            <option value="">All statuses</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="expired">Expired only</option>
          </FormControl>
          <FormControl
            componentClass="select"
            value={campaignChannelFilter}
            onChange={(e) => {
              setCampaignChannelFilter(e.target.value);
              loadCampaigns(1, { channel: e.target.value });
            }}
          >
            <option value="">All channels</option>
            <option value="push">Push</option>
            <option value="email">Email</option>
            <option value="sms">Sms</option>
          </FormControl>
        </div>
        <Table responsive striped hover className="addon-engine-sticky-table">
          <thead><tr><th>Name</th><th>Channels</th><th>Messages</th><th>App Content</th><th>Expires On</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {campaigns.map((campaign) => {
              const journey = campaign.messaging_journeys && campaign.messaging_journeys[0];
              const channels = campaignChannels(campaign);
              const appContentEnabled = campaignAppContentEnabled(campaign);
              return (
                <tr
                  key={campaign.id}
                  className="addon-engine-clickable-row"
                  title="Click to view and edit this retention campaign"
                  onClick={() => editCampaign(campaign)}
                >
                  <td>{campaign.name}</td>
                  <td>
                    <div className="addon-engine-channel-list">
                      {channels.length ? channels.map((channel) => <span key={channel}>{channel}</span>) : "-"}
                    </div>
                  </td>
                  <td>{journey && journey.steps ? journey.steps.filter((step) => step.is_active).length : 0}</td>
                  <td><Status value={appContentEnabled ? "enabled" : "not added"} /></td>
                  <td>
                    {campaignExpiryLabel(campaign)}
                    {isCampaignExpired(campaign) && <div className="addon-engine-muted">Expired</div>}
                  </td>
                  <td><Status value={campaignLifecycleStatus(campaign)} /></td>
                  <td>
                    <div className="addon-engine-actions">
	                      <Button bsStyle="default" bsSize="xsmall" onClick={(event) => { event.stopPropagation(); editCampaign(campaign); }}>View</Button>
	                      <Button bsStyle="info" bsSize="xsmall" onClick={(event) => { event.stopPropagation(); duplicateCampaign(campaign); }}>Duplicate</Button>
	                      <Button bsStyle="danger" bsSize="xsmall" onClick={(event) => { event.stopPropagation(); deleteCampaign(campaign); }}>Remove</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!campaigns.length && (
              <tr>
                <td colSpan={7} className="addon-engine-empty">No retention campaigns match this search.</td>
              </tr>
            )}
          </tbody>
        </Table>
        <div className="addon-engine-pagination">
          <span>
            Showing {from} - {to} of {total}
          </span>
          <div className="addon-engine-pager">
            <Button bsSize="small" disabled={loading || page <= 1} onClick={() => loadCampaigns(page - 1)}>Previous</Button>
            <span>Page {page} of {totalPages}</span>
            <Button bsSize="small" disabled={loading || page >= totalPages} onClick={() => loadCampaigns(page + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </>
  );
}

function renderCustomerRecordTable(rows, loading, pagination, setPage) {
  const page = pagination && pagination.page ? pagination.page : 1;
  const totalPages = pagination && pagination.total_pages ? pagination.total_pages : 1;
  const total = pagination && typeof pagination.total !== "undefined" ? pagination.total : rows.length;
  const from = pagination && typeof pagination.from !== "undefined" ? pagination.from : (rows.length ? 1 : 0);
  const to = pagination && typeof pagination.to !== "undefined" ? pagination.to : rows.length;

  return (
    <>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th>Channel</th>
            <th>Status</th>
            <th>Reason</th>
            <th>Sent At</th>
            <th>Opened</th>
            <th>Clicked</th>
            <th>Converted</th>
            <th>Benefit Used</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((message) => (
            <tr key={message.log_id}>
              <td>
                <strong>{message.customer_name || `Lead #${message.lead_id || "-"}`}</strong>
                <div className="addon-engine-muted">{message.customer_email || message.customer_phone || message.recipient || "-"}</div>
              </td>
              <td>
                <strong>{message.channel}</strong>
                <div className="addon-engine-muted">{message.step || "Retention message"}</div>
              </td>
              <td><Status value={message.status} /></td>
              <td>{message.error_message || "-"}</td>
              <td>{displayDate(message.sent_at || message.scheduled_at)}</td>
              <td>{displayDate(message.opened_at)}</td>
              <td>{displayDate(message.clicked_at)}</td>
              <td>
                {yesNo(message.converted)}
                {message.converted_order_id && <div className="addon-engine-muted">Order #{message.converted_order_id}</div>}
              </td>
              <td>
                {yesNo(message.benefit_utilized)}
                {message.benefit_order_id && <div className="addon-engine-muted">Order #{message.benefit_order_id}</div>}
                {message.benefit_value && <div className="addon-engine-muted">{displayBenefitValue(message.benefit_value)}</div>}
              </td>
            </tr>
          ))}
          {!rows.length && !loading && (
            <tr>
              <td colSpan={9} className="addon-engine-empty">No customer records found for this number yet.</td>
            </tr>
          )}
          {loading && !rows.length && (
            <tr>
              <td colSpan={9} className="addon-engine-empty">Loading customer records...</td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="addon-engine-pagination">
        <span>
          Showing {from} - {to} of {total}
        </span>
        <div className="addon-engine-pager">
          <Button bsSize="small" disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button bsSize="small" disabled={loading || page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </>
  );
}

function renderCampaignMetricReport(report, loadReport) {
  const detail = report.detail || {};
  const messages = detail.messages || [];
  const pagination = detail.pagination || {};
  const displayTotal = typeof pagination.total !== "undefined" ? pagination.total : report.metric.value;

  return (
    <>
      <div className="addon-engine-toolbar">
        <div>
          <strong>{displayTotal || 0} records</strong>
          <p className="addon-engine-muted">Customer list for {report.metric.label.toLowerCase()} in this retention campaign.</p>
        </div>
        <div className="addon-engine-report-controls">
          <FormControl
            value={report.search}
            onChange={(event) => loadReport({ search: event.target.value, page: 1 })}
            placeholder="Search customer, phone, email, channel, status, or order"
          />
          <Button bsStyle="primary" disabled={!messages.length} onClick={() => downloadCampaignMessages(messages, `${report.campaign.key || "campaign"}-${report.metric.key}-page-${pagination.page || 1}`)}>Download Current Page</Button>
        </div>
      </div>

      {report.loading && (
        <div className="addon-engine-guidance">
          <strong>Loading customer records</strong>
          <p>Showing the clicked total now. The customer list will appear here as soon as the detailed logs load.</p>
        </div>
      )}

      {detail.unavailable && (
        <div className="addon-engine-guidance">
          <strong>Customer records not available yet</strong>
          <p>This total exists in the retention summary, but customer-level records are not available for this run. Future retention runs will show the exact customers here once logs are recorded.</p>
        </div>
      )}

      {renderCustomerRecordTable(messages, report.loading, pagination, (page) => loadReport({ page }))}
    </>
  );
}

function renderCampaignDetailReport(report, loadReport) {
  const detail = report.detail || {};
  const summary = detail.summary || {};
  const messages = detail.messages || [];
  const pagination = detail.pagination || {};

  return (
    <>
      <p className="addon-engine-muted">Customer-level retention breakdown for delivery QA, conversion checks, and benefit utilization.</p>

      <div className="addon-engine-summary-grid">
        <div><span>Message Attempts</span><strong>{summary.attempted || 0}</strong></div>
        <div><span>Delivered</span><strong>{summary.delivered || 0}</strong></div>
        <div><span>Failed</span><strong>{summary.failed || 0}</strong></div>
        <div><span>Skipped</span><strong>{summary.skipped || 0}</strong></div>
        <div><span>Email Delivered</span><strong>{summary.email_delivered || 0}</strong></div>
        <div><span>Push Delivered</span><strong>{summary.push_delivered || 0}</strong></div>
        <div><span>Sms Delivered</span><strong>{summary.sms_delivered || 0}</strong></div>
        <div><span>Converted Customers</span><strong>{summary.converted_customers || 0}</strong></div>
        <div><span>Benefit Utilized</span><strong>{summary.benefit_utilized_customers || 0}</strong></div>
      </div>

      <div className="addon-engine-report-controls">
        <FormControl value={report.search} onChange={(event) => loadReport({ search: event.target.value, page: 1 })} placeholder="Search customer, phone, email, channel, status, or order" />
        <FormControl componentClass="select" value={report.channel} onChange={(event) => loadReport({ channel: event.target.value, page: 1 })}>
          <option value="">All channels</option>
          <option value="push">Push</option>
          <option value="email">Email</option>
          <option value="sms">Sms</option>
        </FormControl>
        <FormControl componentClass="select" value={report.status} onChange={(event) => loadReport({ status: event.target.value, page: 1 })}>
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </FormControl>
        <Button bsStyle="primary" disabled={!messages.length} onClick={() => downloadCampaignMessages(messages, `${report.campaign.key || "campaign"}-breakdown-page-${pagination.page || 1}`)}>Download Current Page</Button>
      </div>

      {report.loading && (
        <div className="addon-engine-guidance">
          <strong>Loading customer breakdown</strong>
          <p>Opening the retention totals now. Customer-level delivery records will appear here as soon as they load.</p>
        </div>
      )}

      {detail.unavailable && (
        <div className="addon-engine-guidance">
          <strong>Customer records not available yet</strong>
          <p>This retention campaign has summary totals, but message-level delivery records are not available for this run. Future retention runs will show each customer, channel, delivery status, engagement, and benefit usage here once logs are recorded.</p>
        </div>
      )}

      {renderCustomerRecordTable(messages, report.loading, pagination, (page) => loadReport({ page }))}
    </>
  );
}

function renderCampaignInsights(insights, refresh, openDetail, openMetric, loading = false, setPage = () => {}, search = "", setSearch = () => {}) {
  const summary = insights.summary || {};
  const rows = insights.campaigns || [];
  const pagination = insights.pagination || {};
  const page = pagination.page || 1;
  const totalPages = pagination.total_pages || 1;
  const total = typeof pagination.total !== "undefined" ? pagination.total : rows.length;
  const from = typeof pagination.from !== "undefined" ? pagination.from : (rows.length ? 1 : 0);
  const to = typeof pagination.to !== "undefined" ? pagination.to : rows.length;

  return (
    <>
      <div className="addon-engine-toolbar">
        <div>
          <strong>Retention Insights</strong>
          <p className="addon-engine-muted">Track message delivery, email engagement, push engagement, and orders after retention contact.</p>
        </div>
        <div className="addon-engine-actions">
          <Button bsStyle="default" onClick={refresh}>Refresh</Button>
        </div>
      </div>

      <div className="addon-engine-summary-grid">
        <div><span>Retention Campaigns</span><strong>{summary.campaigns || 0}</strong></div>
        <div><span>Delivered Messages</span><strong>{summary.sent || 0}</strong></div>
        <div><span>Email Opens</span><strong>{summary.email_opened || 0}</strong></div>
        <div><span>Email Clicks</span><strong>{summary.email_clicked || 0}</strong></div>
        <div><span>Push Clicks</span><strong>{summary.push_clicked || 0}</strong></div>
        <div><span>Converted Leads</span><strong>{summary.converted_leads || 0}</strong></div>
        <div><span>Benefit Used</span><strong>{summary.benefit_utilized_customers || 0}</strong></div>
      </div>

      <div className="addon-engine-table-controls">
        <FormControl
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1, event.target.value);
          }}
          placeholder="Search by retention name"
        />
      </div>

      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead>
          <tr>
            <th>Retention</th>
            <th>Delivered</th>
            <th>Failed</th>
            <th>Email Sent</th>
            <th>Push Sent</th>
            <th>Sms Sent</th>
            <th>Email Open</th>
            <th>Email Click</th>
            <th>Push Click</th>
            <th>Converted</th>
            <th>Benefit Used</th>
            <th>Conversion</th>
            <th>Breakdown</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const totals = row.totals || {};
            const rates = row.rates || {};
            const metricButton = (key, label, value, text) => (
              <button
                className="addon-engine-metric-button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  openMetric(row, { key, label, value: value || 0 });
                }}
              >
                {text || value || 0}
              </button>
            );

            return (
              <tr key={row.campaign_id} className="addon-engine-clickable-row" title="Click to view customer breakdown" onClick={() => openDetail(row)}>
                <td>
                  <strong>{row.campaign_name}</strong>
                </td>
                <td>{metricButton("delivered", "Delivered", totals.sent)}</td>
                <td>{metricButton("failed", "Failed", totals.failed)}</td>
                <td>{metricButton("email_sent", "Email Sent", totals.email_sent)}</td>
                <td>{metricButton("push_sent", "Push Sent", totals.push_sent)}</td>
                <td>{metricButton("sms_sent", "Sms Sent", totals.sms_sent)}</td>
                <td>{metricButton("email_open", "Email Open", totals.email_opened, <>{totals.email_opened || 0} <span className="addon-engine-muted">({rates.email_open_rate || 0}%)</span></>)}</td>
                <td>{metricButton("email_click", "Email Click", totals.email_clicked, <>{totals.email_clicked || 0} <span className="addon-engine-muted">({rates.email_click_rate || 0}%)</span></>)}</td>
                <td>{metricButton("push_click", "Push Click", totals.push_clicked, <>{totals.push_clicked || 0} <span className="addon-engine-muted">({rates.push_click_rate || 0}%)</span></>)}</td>
                <td>{metricButton("converted", "Converted", totals.converted_leads)}</td>
                <td>{metricButton("benefit_used", "Benefit Used", totals.benefit_utilized_customers)}</td>
                <td>{metricButton("conversion", "Converted", totals.converted_leads, `${rates.conversion_rate || 0}%`)}</td>
                <td><Button bsStyle="default" bsSize="xsmall" onClick={(event) => { event.stopPropagation(); openDetail(row); }}>View</Button></td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="addon-engine-pagination">
        <span>
          Showing {from} - {to} of {total}
        </span>
        <div className="addon-engine-pager">
          <Button bsSize="small" disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button bsSize="small" disabled={loading || page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>

      <div className="addon-engine-guidance">
        <strong>How to read this</strong>
        <p>Delivered means message logs with status sent across email, push, and Sms. Failed is separate. Email opens can be blocked by email apps, so clicks, conversions, and benefit utilization are stronger QA signals.</p>
      </div>
    </>
  );
}

function renderEntitlements(rows, openModal, deleteRecord) {
  return (
    <>
      <div className="addon-engine-toolbar"><strong>{rows.length} assignments</strong><Button bsStyle="primary" onClick={openModal}>Assign Add-on</Button></div>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Add-on</th><th>Target</th><th>Source</th><th>Starts</th><th>Expires</th><th>Status</th><th>Payment</th><th>Action</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id}><td>{row.addon && row.addon.name}</td><td>{row.target_type} #{row.target_id}</td><td>{row.source_type}</td><td>{row.starts_at || "-"}</td><td>{row.expires_at || "No expiry"}</td><td><Status value={row.status} /></td><td>{row.payment_status || "-"}</td><td><Button bsStyle="danger" bsSize="xsmall" onClick={() => deleteRecord("addon-entitlements", row.id, "assignment")}>Delete</Button></td></tr>)}</tbody>
      </Table>
    </>
  );
}

function renderRules(rows, openModal, deleteRecord, orderId, setOrderId, previewOrder, preview) {
  return (
    <>
      <div className="addon-engine-toolbar"><strong>{rows.length} benefit rules</strong><Button bsStyle="primary" onClick={openModal}>Create Rule</Button></div>
      <div className="addon-engine-qa-panel">
        <div>
          <strong>Order Benefit QA</strong>
          <p className="addon-engine-muted">Preview what add-on benefit will apply before changing an order status.</p>
        </div>
        <div className="addon-engine-inline-form">
          <FormControl value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order ID" />
          <Button bsStyle="default" onClick={previewOrder}>Preview</Button>
        </div>
      </div>
      {preview && (
        <div className="addon-engine-preview">
          <div><span>Order</span><strong>#{preview.order && preview.order.id}</strong></div>
          <div><span>Lead</span><strong>{preview.order && preview.order.lead_id ? `#${preview.order.lead_id}` : "-"}</strong></div>
          <div><span>Current Total</span><strong>{preview.order ? preview.order.current_total : "-"}</strong></div>
          <div><span>Final Total</span><strong>{preview.preview ? preview.preview.final_amount : "-"}</strong></div>
          <div><span>Already Applied</span><strong>{preview.already_applied ? "Yes" : "No"}</strong></div>
          <div><span>Selected Benefits</span><strong>{preview.preview && preview.preview.selected ? preview.preview.selected.length : 0}</strong></div>
        </div>
      )}
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Name</th><th>Add-on</th><th>Benefit</th><th>Order</th><th>Trigger</th><th>Value</th><th>Usage/User</th><th>Policy</th><th>Status</th><th>Action</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id}><td>{row.name}</td><td>{row.addon && row.addon.name}</td><td>{row.benefit_type}</td><td>{row.order_type}</td><td>{row.trigger_type} {row.trigger_value || ""}</td><td>{row.discount_value}</td><td>{row.usage_limit_per_user || "-"}</td><td>{row.stacking_policy}</td><td><Status value={row.is_active} /></td><td><Button bsStyle="danger" bsSize="xsmall" onClick={() => deleteRecord("addon-benefit-rules", row.id, "benefit rule")}>Delete</Button></td></tr>)}</tbody>
      </Table>
    </>
  );
}

function renderExpiryReport(report, filters, setFilters, refresh, addons) {
  const summary = report.summary || {};
  const records = report.records || [];

  return (
    <>
      <div className="addon-engine-toolbar">
        <strong>{records.length} expiry records</strong>
        <Button bsStyle="default" onClick={refresh}>Refresh</Button>
      </div>
      <div className="addon-engine-summary-grid">
        <div><span>Active</span><strong>{summary.active || 0}</strong></div>
        <div><span>Expiring</span><strong>{summary.expiring || 0}</strong></div>
        <div><span>Expired</span><strong>{summary.expired || 0}</strong></div>
        <div><span>Paid Success</span><strong>{summary.paid_success || 0}</strong></div>
      </div>
      <div className="addon-engine-filter-row">
        <Field label="Status">
          <FormControl componentClass="select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All</option>
            <option value="expiring">Expiring Soon</option>
            <option value="expired">Expired</option>
            <option value="active">Active</option>
          </FormControl>
        </Field>
        <Field label="Expiring Days">
          <FormControl type="number" value={filters.expiring_days} onChange={(e) => setFilters({ ...filters, expiring_days: e.target.value })} />
        </Field>
        <Field label="Add-on">
          <FormControl componentClass="select" value={filters.addon_id} onChange={(e) => setFilters({ ...filters, addon_id: e.target.value })}>
            <option value="">All add-ons</option>
            {addons.map((addon) => <option key={addon.id} value={addon.id}>{addon.name}</option>)}
          </FormControl>
        </Field>
        <Field label="Payment">
          <FormControl componentClass="select" value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}>
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </FormControl>
        </Field>
      </div>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Add-on</th><th>Target</th><th>Source</th><th>Starts</th><th>Expires</th><th>Status</th><th>Payment</th></tr></thead>
        <tbody>
          {records.map((row) => (
            <tr key={row.id}>
              <td>{row.addon && row.addon.name}</td>
              <td>{row.target_type} #{row.target_id}</td>
              <td>{row.source_type}</td>
              <td>{row.starts_at || "-"}</td>
              <td>{row.expires_at || "No expiry"}</td>
              <td><Status value={row.status} /></td>
              <td>{row.payment_status || "-"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function renderJourneys(rows, openModal, deleteRecord) {
  return (
    <>
      <div className="addon-engine-toolbar"><strong>{rows.length} journeys</strong><Button bsStyle="primary" onClick={openModal}>Create Journey</Button></div>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Key</th><th>Name</th><th>Add-on</th><th>Audience</th><th>Trigger</th><th>Steps</th><th>Status</th><th>Action</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={row.id}><td>{row.key}</td><td>{row.name}</td><td>{row.addon && row.addon.name}</td><td>{row.audience_type}</td><td>{row.trigger_type} {row.trigger_key || ""}</td><td>{row.steps ? row.steps.length : 0}</td><td><Status value={row.is_active} /></td><td><Button bsStyle="danger" bsSize="xsmall" onClick={() => deleteRecord("messaging-journeys", row.id, "messaging journey")}>Delete</Button></td></tr>)}</tbody>
      </Table>
    </>
  );
}

function renderUsage(logs, benefits) {
  return (
    <>
      <h5>Usage Logs</h5>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Add-on</th><th>Lead</th><th>Order</th><th>Type</th><th>Value</th><th>Used At</th></tr></thead>
        <tbody>{logs.map((row) => <tr key={row.id}><td>{row.addon && row.addon.name}</td><td>{row.lead_id || "-"}</td><td>{row.order_id || "-"}</td><td>{row.usage_type}</td><td>{row.value_applied || "-"}</td><td>{row.used_at || row.created_at}</td></tr>)}</tbody>
      </Table>
      <h5>Order Benefit Snapshots</h5>
      <Table responsive striped hover className="addon-engine-sticky-table">
        <thead><tr><th>Order</th><th>Lead</th><th>Add-on</th><th>Benefit</th><th>Discount</th><th>Delivery</th><th>Final Snapshot</th></tr></thead>
        <tbody>{benefits.map((row) => <tr key={row.id}><td>{row.order_id}</td><td>{row.lead_id}</td><td>{row.addon && row.addon.name}</td><td>{row.benefit_type}</td><td>{row.discount_amount}</td><td>{row.delivery_discount}</td><td>{row.final_amount_snapshot}</td></tr>)}</tbody>
      </Table>
    </>
  );
}

function renderAddonForm(form, setForm) {
  const applyTemplate = (template) => {
    if (template === "paid_feature") {
      setForm({
        ...form,
        key: form.key || "biomarkers",
        name: form.name || "Biomarkers",
        type: "access",
        activation_type: "customer_purchase",
        target_scope: "lead",
        price: form.price || 100,
        duration_value: form.duration_value || 1,
        duration_type: form.duration_type || "month",
        is_active: 1,
      });
    }

    if (template === "admin_benefit") {
      setForm({
        ...form,
        type: "benefit",
        activation_type: "admin_assign",
        target_scope: "lead",
        price: 0,
        is_active: 1,
      });
    }

    if (template === "messaging") {
      setForm({
        ...form,
        type: "automation",
        activation_type: "admin_assign",
        target_scope: "organization",
        price: 0,
        is_active: 1,
      });
    }
  };

  return (
    <>
      <div className="addon-engine-guidance">
        <strong>Choose the closest setup</strong>
        <p>These shortcuts only fill the form. You can still change any value before saving.</p>
        <div className="addon-engine-template-row">
          <Button bsStyle="default" onClick={() => applyTemplate("paid_feature")}>Paid App Feature</Button>
          <Button bsStyle="default" onClick={() => applyTemplate("admin_benefit")}>Admin Benefit</Button>
          <Button bsStyle="default" onClick={() => applyTemplate("messaging")}>Messaging Module</Button>
        </div>
      </div>
      <div className="addon-engine-form-grid">
        <Field label="Key">
          <FormControl value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="biomarkers" />
          <Hint>Use a short unique code. The app team will use this key.</Hint>
        </Field>
        <Field label="Name">
          <FormControl value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Hint>This is what admin users will recognize.</Hint>
        </Field>
        <Field label="Type">
          <FormControl componentClass="select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="access">Access</option><option value="benefit">Benefit</option><option value="automation">Automation</option></FormControl>
          <Hint>Access unlocks features. Benefit affects orders. Automation controls journeys.</Hint>
        </Field>
        <Field label="Activation">
          <FormControl componentClass="select" value={form.activation_type} onChange={(e) => setForm({ ...form, activation_type: e.target.value })}><option value="customer_purchase">Customer Purchase</option><option value="admin_assign">Admin Assign</option><option value="system_campaign">System Campaign</option><option value="organization_enabled">Organization Enabled</option></FormControl>
          <Hint>Choose who enables it: customer, admin, campaign, or organization.</Hint>
        </Field>
        <Field label="Target">
          <FormControl componentClass="select" value={form.target_scope} onChange={(e) => setForm({ ...form, target_scope: e.target.value })}><option value="lead">Lead</option><option value="organization">Organization</option><option value="lead_group">Lead Group</option><option value="admin_user">Admin User</option></FormControl>
          <Hint>For app purchases, Lead is usually correct.</Hint>
        </Field>
        <Field label="Price">
          <FormControl type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <Hint>Keep 0 for manually assigned free benefits.</Hint>
        </Field>
        <Field label="Duration Value">
          <FormControl type="number" value={form.duration_value} onChange={(e) => setForm({ ...form, duration_value: e.target.value })} />
          <Hint>Example: 1 month, 30 days, or blank for no expiry.</Hint>
        </Field>
        <Field label="Duration Type">
          <FormControl componentClass="select" value={form.duration_type} onChange={(e) => setForm({ ...form, duration_type: e.target.value })}><option value="">No expiry</option><option value="day">Day</option><option value="week">Week</option><option value="month">Month</option><option value="year">Year</option><option value="lifetime">Lifetime</option></FormControl>
          <Hint>Expiry report depends on this when access is purchased.</Hint>
        </Field>
        <Field label="Status">
          <FormControl componentClass="select" value={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.value })}><option value={1}>Active</option><option value={0}>Inactive</option></FormControl>
          <Hint>Inactive add-ons are hidden from purchase/access checks.</Hint>
        </Field>
        <Field label="Description">
          <FormControl componentClass="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Hint>Short internal note for admin users.</Hint>
        </Field>
      </div>
    </>
  );
}

function renderEntitlementForm(form, setForm, addonOptions) {
  return (
    <div className="addon-engine-form-grid">
      <Field label="Add-on"><FormControl componentClass="select" value={form.addon_id} onChange={(e) => setForm({ ...form, addon_id: e.target.value })}><option value="">Select</option>{addonOptions}</FormControl></Field>
      <Field label="Target Type"><FormControl componentClass="select" value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })}><option value="lead">Lead</option><option value="organization">Organization</option><option value="lead_group">Lead Group</option><option value="admin_user">Admin User</option></FormControl></Field>
      <Field label="Target ID"><FormControl value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} /></Field>
      <Field label="Source"><FormControl componentClass="select" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })}><option value="admin_assign">Admin Assign</option><option value="customer_purchase">Customer Purchase</option><option value="system_campaign">System Campaign</option><option value="package_grant">Package Grant</option></FormControl></Field>
      <Field label="Starts At"><FormControl type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></Field>
      <Field label="Expires At"><FormControl type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></Field>
      <Field label="Status"><FormControl componentClass="select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="active">Active</option><option value="paused">Paused</option><option value="cancelled">Cancelled</option></FormControl></Field>
      <Field label="Payment Status"><FormControl componentClass="select" value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })}><option value="">Not needed</option><option value="pending">Pending</option><option value="paid">Paid</option><option value="failed">Failed</option></FormControl></Field>
    </div>
  );
}

function renderRuleForm(form, setForm, addonOptions) {
  return (
    <div className="addon-engine-form-grid">
      <Field label="Add-on"><FormControl componentClass="select" value={form.addon_id} onChange={(e) => setForm({ ...form, addon_id: e.target.value })}><option value="">Select</option>{addonOptions}</FormControl></Field>
      <Field label="Name"><FormControl value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="Benefit"><FormControl componentClass="select" value={form.benefit_type} onChange={(e) => setForm({ ...form, benefit_type: e.target.value })}><option value="fixed_discount">Fixed Discount</option><option value="percentage_discount">Percentage Discount</option><option value="free_delivery">Free Delivery</option><option value="flash_points">Flash Points</option><option value="claim_credit">Claim Credit</option></FormControl></Field>
      <Field label="Order Type"><FormControl componentClass="select" value={form.order_type} onChange={(e) => setForm({ ...form, order_type: e.target.value })}><option value="all">All</option><option value="medicine">Medicine</option><option value="lab">Lab</option></FormControl></Field>
      <Field label="Trigger"><FormControl componentClass="select" value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}><option value="manual">Manual</option><option value="first_order">First Order</option><option value="second_order">Second Order</option><option value="nth_order">Nth Order</option><option value="date_range">Date Range</option><option value="refill_due">Refill Due</option></FormControl></Field>
      <Field label="Trigger Value"><FormControl type="number" value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })} /></Field>
      <Field label="Discount/Points Value"><FormControl type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} /></Field>
      <Field label="Max Discount"><FormControl type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} /></Field>
      <Field label="Min Order Value"><FormControl type="number" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} /></Field>
      <Field label="Usage/User"><FormControl type="number" value={form.usage_limit_per_user} onChange={(e) => setForm({ ...form, usage_limit_per_user: e.target.value })} /></Field>
      <Field label="Priority"><FormControl type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} /></Field>
      <Field label="Stacking"><FormControl componentClass="select" value={form.stacking_policy} onChange={(e) => setForm({ ...form, stacking_policy: e.target.value })}><option value="best_only">Best Only</option><option value="stackable">Stackable</option><option value="exclusive">Exclusive</option></FormControl></Field>
    </div>
  );
}

function renderJourneyForm(form, setForm, addonOptions, updateStep, addStep) {
  return (
    <>
      <div className="addon-engine-form-grid">
        <Field label="Add-on"><FormControl componentClass="select" value={form.addon_id} onChange={(e) => setForm({ ...form, addon_id: e.target.value })}><option value="">No add-on gate</option>{addonOptions}</FormControl></Field>
        <Field label="Key"><FormControl value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="chronic_refill_reminder" /></Field>
        <Field label="Name"><FormControl value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Audience"><FormControl componentClass="select" value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value })}><option value="lead">Lead</option><option value="organization">Organization</option><option value="lead_group">Lead Group</option></FormControl></Field>
        <Field label="Trigger Type"><FormControl value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })} /></Field>
        <Field label="Trigger Key"><FormControl value={form.trigger_key} onChange={(e) => setForm({ ...form, trigger_key: e.target.value })} /></Field>
        <Field label="Description"><FormControl componentClass="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
      </div>
      <div className="addon-engine-toolbar"><strong>Journey Steps</strong><Button onClick={addStep}>Add Step</Button></div>
      {form.steps.map((step, index) => (
        <Row key={index}>
          <Col md={1}><Field label="Order"><FormControl type="number" value={step.step_order} onChange={(e) => updateStep(index, "step_order", e.target.value)} /></Field></Col>
          <Col md={2}><Field label="Name"><FormControl value={step.name} onChange={(e) => updateStep(index, "name", e.target.value)} /></Field></Col>
          <Col md={2}><Field label="Channel"><FormControl componentClass="select" value={step.channel} onChange={(e) => updateStep(index, "channel", e.target.value)}><option value="push">Push</option><option value="email">Email</option><option value="whatsapp">WhatsApp</option></FormControl></Field></Col>
          <Col md={1}><Field label="Delay"><FormControl type="number" value={step.delay_value} onChange={(e) => updateStep(index, "delay_value", e.target.value)} /></Field></Col>
          <Col md={2}><Field label="Delay Type"><FormControl componentClass="select" value={step.delay_type} onChange={(e) => updateStep(index, "delay_type", e.target.value)}><option value="minute">Minute</option><option value="hour">Hour</option><option value="day">Day</option></FormControl></Field></Col>
          <Col md={4}><Field label="Message"><FormControl value={step.message_body} onChange={(e) => updateStep(index, "message_body", e.target.value)} /></Field></Col>
        </Row>
      ))}
    </>
  );
}

export default AddonEngine;
